import { useCallback, useEffect, useMemo, useRef, useState, type ClipboardEvent } from 'react'
import { createPortal } from 'react-dom'
import { Alert, Avatar, Badge, Button, DatePicker, Empty, Form, Input, List, Modal, Popconfirm, Popover, Select, Space, Spin, Switch, Tag, Tooltip, Typography, message } from 'antd'
import { CheckCircleOutlined, CheckOutlined, CloseOutlined, DeleteOutlined, DownOutlined, DownloadOutlined, EditOutlined, FileOutlined, InfoCircleOutlined, PaperClipOutlined, PushpinOutlined, QuestionCircleOutlined, ReloadOutlined, RotateLeftOutlined, RotateRightOutlined, SearchOutlined, SendOutlined, SettingOutlined, SmileOutlined, StopOutlined, SwapOutlined, UserOutlined, ZoomInOutlined, ZoomOutOutlined } from '@ant-design/icons'
import dayjs, { type Dayjs } from 'dayjs'
import { useNavigate, useNavigationType, useParams, useSearchParams } from 'react-router-dom'
import { leadsApi, type AvailableReaction, type GeoFolder, type Lead, type LeadMessage, type MessageReaction, type MessageSearchHit, type SenderAccount, type SenderAccounts } from '../services/leadsApi'
import { chatsApi, operatorsApi } from '../services/api'
import { chatFoldersApi, type ChatFolder } from '../services/chatFoldersApi'
import { chatWorkspaceApi, type ChatWorkItem, type CloseReasonGroup, type LeadDraft, type WorkTab } from '../services/chatWorkspaceApi'
import { leadNotesApi, type LeadNote } from '../services/leadNotesApi'
import { messageTemplatesApi, type MessageTemplate } from '../services/messageTemplatesApi'
import { useLeadChatEventsStore } from '../store/leadChatEventsStore'
import { DuplicateChip, DuplicateNotice, LeadDraftDuplicate } from '../components/Chats/DuplicateNotice'
import PlainChatWindow from '../components/Chats/PlainChatWindow'
import { useAuthStore } from '../store/authStore'
import Contacts from './Contacts'
import { usePageSearchStore } from '../store/pageSearchStore'
import { DEFAULT_TEMPERATURE, TEMPERATURE_OPTIONS, temperatureColor, temperatureLabel } from '../constants/leadTemperature'
import { REACTION_KEYWORDS } from '../constants/reactionSearch'
import TgRichEditor, { type TgRichEditorHandle } from '../components/TgRichEditor'
import FormattedText from '../components/FormattedText'
import { messageAuthorTooltip } from '../utils/messageAuthor'
import { hasTgFormatting, plainToTg, templateToTg, tgToPlain } from '../utils/tgFormat'

// Ширина выезжающей карточки лида в разделе «Чаты». На неё сжимается
// переписка, поэтому значение одно на оба места.
const CARD_DRAWER_WIDTH = 560

const ALL_FOLDERS_VALUE = 0

const WORK_TABS: { key: WorkTab; label: string }[] = [
    { key: 'replied', label: 'Ответили' },
    { key: 'active', label: 'Активные' },
    { key: 'postponed', label: 'Отложенные' },
    { key: 'closed', label: 'Закрытые' },
]

// Вкладка, в которой лежит диалог: на неё переключаемся, открывая его по ссылке.
function workTabOf(item: ChatWorkItem): WorkTab {
    if (item.work_state === 'active' || item.work_state === 'postponed' || item.work_state === 'closed') {
        return item.work_state
    }
    return 'replied'
}

const FOLDER_COLOR_PRESETS = ['#3b82f6', '#eab308', '#ef4444', '#22c55e', '#a855f7', '#f97316', '#14b8a6', '#ec4899']

const { Text, Title } = Typography
const { TextArea } = Input
const ALL_GEOS_VALUE = '__all_geos__'
// Совпадает с ограничением на сервере: триграммный индекс работает от трёх символов.
const SEARCH_MIN_LENGTH = 3
const SEARCH_HITS_LIMIT = 30

// Telegram хранит часть реакций без селектора варианта U+FE0F, а клавиатуры его
// добавляют. Сравниваем по нормализованной строке, отправляем ту, что дал Telegram.
const normalizeReaction = (emoji: string) => emoji.replace(/\uFE0F/g, '')

// У этих символов презентация по умолчанию текстовая: без селектора U+FE0F
// браузер рисует их одноцветными под цвет текста, а не как эмодзи. Telegram
// отдаёт их без селектора, поэтому возвращаем его только для показа - в запросы
// уходит строка ровно та, что дал сервер.
const TEXT_PRESENTATION_CHARS = ['\u2764', '\u270D', '\u2603', '\u{1F54A}', '\u2640', '\u2642']

const displayReaction = (emoji: string) => {
    let result = emoji
    for (const char of TEXT_PRESENTATION_CHARS) {
        result = result.split(char + '\uFE0F').join(char).split(char).join(char + '\uFE0F')
    }
    return result
}

// Быстрый ряд реакций подстраивается под оператора: последние использованные
// поднимаются в начало, как в Telegram.
const DEFAULT_QUICK_REACTIONS = ['\u{1F44D}', '\u2764\uFE0F', '\u{1F525}', '\u{1F601}', '\u{1F622}', '\u{1F64F}']
const QUICK_REACTIONS_KEY = 'tg_recent_reactions'
const QUICK_REACTIONS_LIMIT = 6

function readQuickReactions(): string[] {
    try {
        const saved = JSON.parse(localStorage.getItem(QUICK_REACTIONS_KEY) || '[]')
        if (Array.isArray(saved) && saved.length) return saved.slice(0, QUICK_REACTIONS_LIMIT)
    } catch {
        // повреждённое значение просто игнорируем
    }
    return DEFAULT_QUICK_REACTIONS
}

type ReactionAuthor = { key: string; title: string; avatar?: string | null; initials: string }

// Кто поставил реакцию. Отдельных данных для этого не нужно: в переписке
// на двоих chosen однозначно указывает на наш рабочий аккаунт.
function reactionAuthors(reaction: MessageReaction, message: LeadMessage, lead: Lead | null): ReactionAuthor[] {
    const authors: ReactionAuthor[] = []
    const accountName = message.account_username ? `@${message.account_username.replace(/^@/, '')}` : (message.account_phone || '')

    if (reaction.chosen) {
        authors.push({
            key: 'own',
            title: accountName ? `Вы · ${accountName}` : 'Вы',
            avatar: message.account_avatar_url,
            initials: (accountName.replace(/^@/, '')[0] || 'Я').toUpperCase(),
        })
    }
    // count > 1 при своей реакции означает, что тем же эмодзи ответил и лид.
    if (!reaction.chosen || reaction.count > 1) {
        const name = lead ? leadName(lead) : 'Лид'
        authors.push({
            key: 'lead',
            title: name,
            avatar: lead?.telegram_avatar_url,
            initials: name.slice(0, 1).toUpperCase() || 'Л',
        })
    }
    return authors
}

// Тот же пересчёт, что и на сервере: снимаем прежнюю свою реакцию и ставим новую.
// Нужен, чтобы чип появлялся мгновенно, не дожидаясь ответа.
function applyOwnReaction(current: MessageReaction[] | null | undefined, emoji: string): MessageReaction[] | null {
    const items = (current || []).map((item) => ({ ...item }))
    for (const item of items) {
        if (item.chosen) {
            item.count = Math.max(0, item.count - 1)
            item.chosen = false
        }
    }
    const kept = items.filter((item) => item.count > 0)

    if (emoji) {
        const existing = kept.find((item) => item.emoji === emoji)
        if (existing) {
            existing.count += 1
            existing.chosen = true
        } else {
            kept.push({ emoji, count: 1, chosen: true })
        }
    }

    return kept.length ? kept : null
}

// Быстрые сроки напоминания: закрывают почти все случаи без календаря.
const REMINDER_PRESETS: { label: string; value: () => Dayjs }[] = [
    { label: 'Через час', value: () => dayjs().add(1, 'hour') },
    { label: 'Через 3 часа', value: () => dayjs().add(3, 'hour') },
    { label: 'Сегодня в 18:00', value: () => dayjs().hour(18).minute(0).second(0) },
    { label: 'Завтра в 10:00', value: () => dayjs().add(1, 'day').hour(10).minute(0).second(0) },
    { label: 'Через 3 дня', value: () => dayjs().add(3, 'day').hour(10).minute(0).second(0) },
    { label: 'Через неделю', value: () => dayjs().add(7, 'day').hour(10).minute(0).second(0) },
]

type ImagePreviewState = {
    url: string
    name: string
    scale: number
    rotation: number
}

const EMOJI_GROUPS = [
    {
        title: '😀 Смайлы',
        keywords: 'улыбка смех радость грусть лицо эмоции',
        emojis: ['😀', '😃', '😄', '😁', '😆', '😅', '🤣', '😂', '🙂', '🙃', '😉', '😊', '😇', '🥰', '😍', '🤩', '😘', '😋', '😎', '🤔', '😐', '😒', '🙄', '😬', '😴', '😢', '😭', '😤', '😡', '🤯'],
    },
    {
        title: '❤️ Жесты и сердца',
        keywords: 'сердце лайк рука жест ок спасибо любовь',
        emojis: ['❤️', '🧡', '💛', '💚', '💙', '💜', '🖤', '🤍', '🤎', '💔', '💕', '💞', '💖', '💘', '👍', '👎', '👌', '✌️', '🤝', '🙏', '👏', '🙌', '💪', '👋', '✍️'],
    },
    {
        title: '✅ Работа',
        keywords: 'работа офис задача чек файл письмо телефон деньги',
        emojis: ['✅', '❌', '❓', '❗', '💯', '🔥', '⭐', '✨', '📌', '📎', '📄', '📁', '📊', '📈', '📉', '💬', '📞', '☎️', '📧', '💰', '💳', '⏰', '🕐', '🚀', '🔒', '🔓'],
    },
    {
        title: '🎉 Символы',
        keywords: 'символ праздник подарок музыка спорт еда',
        emojis: ['🎉', '🎊', '🎁', '🏆', '🥇', '🎯', '🎮', '🎧', '🎵', '🍀', '☀️', '🌙', '⚡', '🌟', '🍕', '☕', '🍾', '✈️', '🚗', '🏠', '🏢'],
    },
]

const EMOJI_KEYWORDS: Record<string, string> = {
    '😀': 'улыбка счастливый радость',
    '😂': 'смех смешно',
    '🤣': 'смеюсь ржу',
    '😊': 'улыбка приятно',
    '😍': 'любовь нравится',
    '🤔': 'думаю вопрос',
    '😎': 'круто',
    '😢': 'грусть печаль',
    '😭': 'плачу',
    '😡': 'злость',
    '👍': 'лайк хорошо ок да',
    '👎': 'плохо нет',
    '🙏': 'спасибо пожалуйста',
    '🤝': 'сделка договор',
    '✅': 'готово выполнено да',
    '❌': 'нет отмена ошибка',
    '📎': 'вложение файл прикрепить',
    '💰': 'деньги оплата',
    '🔥': 'огонь срочно',
}

function leadName(lead: Lead): string {
    return lead.title || [lead.first_name, lead.last_name].filter(Boolean).join(' ') || lead.telegram_nickname || leadRef(lead)
}

function leadUsername(lead: Lead): string {
    const value = lead.telegram_username || lead.telegram_nickname || ''
    return value.startsWith('@') ? value : value ? `@${value}` : 'Telegram не указан'
}

// Чем лид называется, когда ник и телефон скрыты: идентификатор его карточки
// в CRM Wiseway, а у заведённых вручную - наш номер.
function leadRef(lead: Pick<Lead, 'id' | 'crm_ww_id'>): string {
    const crmId = (lead.crm_ww_id || '').trim()
    return crmId ? `ID CRM WW ${crmId}` : `Лид #${lead.id}`
}

function extractError(error: unknown): string {
    if (typeof error === 'object' && error && 'response' in error) {
        const response = (error as { response?: { data?: { detail?: string } } }).response
        return response?.data?.detail || 'Ошибка запроса'
    }
    return 'Ошибка запроса'
}

const accountProblemStatuses = [
    'banned_by_telegram',
    'configuration_error',
    'connection_error',
    'error',
    'hard_banned',
    'limited',
    'session_invalid',
    'temporarily_blocked',
]

const accountBlockTypeLabels: Record<string, string> = {
    account_banned: 'Аккаунт заблокирован Telegram',
    account_deactivated: 'Аккаунт деактивирован',
    connection_error: 'Ошибка соединения',
    flood_premium_wait: 'Лимит Telegram Premium',
    flood_premium_wait_unknown_until: 'Лимит Telegram Premium без точного срока',
    flood_test_phone_wait: 'Тестовый flood wait',
    flood_test_phone_wait_unknown_until: 'Тестовый flood wait без точного срока',
    flood_wait: 'Flood wait',
    flood_wait_unknown_until: 'Flood wait без точного срока',
    hard_ban: 'Постоянный бан номера',
    limited: 'Ограничение Telegram',
    password_too_fresh: 'Пароль изменен слишком недавно',
    password_too_fresh_unknown_until: 'Пароль изменен слишком недавно, точный срок не передан',
    recipient_or_privacy: 'Ограничение получателя или приватность',
    session_invalid: 'Сессия недействительна',
    session_too_fresh: 'Сессия слишком новая',
    session_too_fresh_unknown_until: 'Сессия слишком новая, точный срок не передан',
    slowmode_wait: 'Slow mode',
    slowmode_wait_unknown_until: 'Slow mode без точного срока',
    takeout_init_delay: 'Задержка Takeout',
    takeout_init_delay_unknown_until: 'Задержка Takeout без точного срока',
    telegram_configuration_error: 'Ошибка настроек Telegram',
    telegram_rate_limit_unknown_until: 'Лимит Telegram без точного срока',
    temporary_limit: 'Временное ограничение без точного срока',
    two_factor_confirm_wait: 'Ожидание 2FA-подтверждения',
    two_factor_confirm_wait_unknown_until: 'Ожидание 2FA-подтверждения без точного срока',
    unknown_error: 'Неизвестная ошибка',
    peer_flood_spam_block: 'Спам-блок за агрессивную рассылку',
    connection_retry: 'Переподключение аккаунта',
}

function isOperatorBlockedLead(lead: Lead): boolean {
    return Boolean(lead.last_error?.toLowerCase().includes('заблокирован'))
}

function hasAccountProblem(lead: Lead): boolean {
    return Boolean(
        (lead.last_error && !isOperatorBlockedLead(lead)) ||
        accountProblemStatuses.includes(lead.account_health_status || '')
    )
}

// temporarily_blocked - лимит/флуд, аккаунт восстановится сам (жёлтый);
// остальные проблемные статусы (hard_banned/session_invalid/etc) - аккаунт
// сломан и не восстановится без вмешательства (красный).
// Состояние рабочего TG-аккаунта одной строкой, как «● Аккаунт работает» в макете.
// kind: ok - зелёный; temporary - жёлтый, пройдёт само (подробности по нажатию
// на кружок, чат не загораживаем); permanent - красный, само не пройдёт
// (предупреждение прямо в чате).
type AccountHealthKind = 'ok' | 'temporary' | 'permanent'

function accountHealth(item: ChatWorkItem | null, lead: Lead | null): { label: string; color: string; kind: AccountHealthKind } {
    const status = item?.account_health_status || lead?.account_health_status || ''
    if (!status || status === 'healthy' || status === 'unknown') {
        return { label: 'Аккаунт работает', color: 'var(--success)', kind: 'ok' }
    }
    if (status === 'temporarily_blocked' || status === 'limited' || status === 'flood_wait') {
        return { label: 'Временное ограничение Telegram', color: 'var(--warning)', kind: 'temporary' }
    }
    if (status === 'hard_banned' || status === 'banned_by_telegram') {
        return { label: 'Аккаунт заблокирован Telegram', color: 'var(--error)', kind: 'permanent' }
    }
    return { label: 'Сессия недоступна', color: 'var(--error)', kind: 'permanent' }
}

function accountStatusColor(lead: Lead): '#faad14' | '#ff4d4f' {
    return lead.account_health_status === 'temporarily_blocked' ? '#faad14' : '#ff4d4f'
}

function searchEmojis(query: string): string[] {
    const value = query.trim().toLowerCase()
    if (!value) return []
    const results = new Set<string>()
    for (const [emoji, keywords] of Object.entries(EMOJI_KEYWORDS)) {
        if (keywords.toLowerCase().includes(value)) results.add(emoji)
    }
    for (const group of EMOJI_GROUPS) {
        if (group.title.toLowerCase().includes(value) || group.keywords.includes(value)) {
            group.emojis.forEach((emoji) => results.add(emoji))
        }
        group.emojis.forEach((emoji) => {
            if (emoji.includes(value)) results.add(emoji)
        })
    }
    return Array.from(results)
}

function formatFileSize(size?: number | null): string {
    if (!size) return ''
    if (size < 1024) return `${size} Б`
    if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} КБ`
    return `${(size / 1024 / 1024).toFixed(1)} МБ`
}

function clampContextMenuPosition(x: number, y: number) {
    const width = 280
    const height = 246
    return {
        x: Math.max(12, Math.min(x, window.innerWidth - width - 12)),
        y: Math.max(12, Math.min(y, window.innerHeight - height - 12)),
    }
}

function accountLabelFromLead(lead: Lead): string {
    const username = lead.account_username ? (lead.account_username.startsWith('@') ? lead.account_username : `@${lead.account_username}`) : ''
    const accountName = [lead.account_first_name, lead.account_last_name].filter(Boolean).join(' ')
    return [username || lead.account_phone, accountName].filter(Boolean).join(' · ') || 'аккаунт еще не выбран'
}

const SENDER_DOT: Record<SenderAccount['state'], string> = {
    ok: '#52c41a',
    limited: '#faad14',
    unusable: '#ff4d4f',
}

// Строка выпадающего списка «Отправка от»: кружок состояния, аккаунт и
// подсказки - основной ли он, писал ли сюда лид последним, что с ним не так.
function senderOption(account: SenderAccount, lastIncomingAccountId?: number | null) {
    const notes = [
        account.id === lastIncomingAccountId ? 'лид писал сюда последним' : null,
        account.is_main ? 'основной аккаунт диалога' : null,
        account.last_incoming_at ? `последнее от лида ${dayjs(account.last_incoming_at).format('DD.MM HH:mm')}` : null,
    ].filter(Boolean).join(' · ')
    const dot = <span className="chat-health-dot sender-dot" style={{ background: SENDER_DOT[account.state] }} />
    return {
        value: account.id,
        disabled: account.state === 'unusable',
        short: (
            <span className="sender-option-short">
                <strong>{account.label}</strong>
                {dot}
            </span>
        ),
        label: (
            <div className="sender-option">
                {dot}
                <div>
                    <div className="sender-option-name">{account.label}</div>
                    {notes && <div className="sender-note">{notes}</div>}
                    {account.reason && (
                        <div className={`sender-note is-${account.state}`}>
                            {account.state === 'unusable' ? 'недоступен: ' : ''}{account.reason}
                        </div>
                    )}
                </div>
            </div>
        ),
    }
}

function leadStatusLabel(lead: Lead, statuses: { key: string; label: string }[]): string {
    return statuses.find((status) => status.key === lead.status)?.label || lead.status || 'Без статуса'
}

function RussianEmojiPicker({ onSelect }: { onSelect: (emoji: string) => void }) {
    const [query, setQuery] = useState('')
    const results = searchEmojis(query)
    return (
        <div className="lead-emoji-picker">
            <Input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Поиск эмодзи..."
                allowClear
                className="lead-emoji-search"
            />
            <div className="lead-emoji-scroll">
                {query ? (
                    <div className="lead-emoji-section">
                        <Text type="secondary">Результаты</Text>
                        <div className="lead-emoji-grid">
                            {results.map((emoji) => (
                                <button key={emoji} type="button" onClick={() => onSelect(emoji)}>{emoji}</button>
                            ))}
                        </div>
                        {results.length === 0 && <Text type="secondary">Ничего не найдено</Text>}
                    </div>
                ) : (
                    EMOJI_GROUPS.map((group) => (
                        <div className="lead-emoji-section" key={group.title}>
                            <Text type="secondary">{group.title}</Text>
                            <div className="lead-emoji-grid">
                                {group.emojis.map((emoji) => (
                                    <button key={`${group.title}-${emoji}`} type="button" onClick={() => onSelect(emoji)}>{emoji}</button>
                                ))}
                            </div>
                        </div>
                    ))
                )}
            </div>
        </div>
    )
}

function renderLeadMedia(item: LeadMessage, onPreviewImage: (image: { url: string; name: string }) => void) {
    const url = item.media_file_path ? leadsApi.mediaUrl(item.media_file_path) : ''
    const contentType = (item.content_type || '').toLowerCase()
    const mime = (item.media_mime_type || '').toLowerCase()
    const name = item.media_file_name || 'Файл'
    const size = formatFileSize(item.media_size)
    const isImage = Boolean(url) && (contentType === 'photo' || contentType === 'sticker' || mime.startsWith('image/') || /\.(png|jpe?g|gif|webp)$/i.test(name))
    const isVideo = Boolean(url) && (contentType === 'video' || contentType === 'animation' || contentType === 'video_note' || mime.startsWith('video/'))
    const isAudio = Boolean(url) && (contentType === 'audio' || contentType === 'voice' || mime.startsWith('audio/'))

    if (!url && contentType === 'text') return null

    if (isImage) {
        return (
            <div className="lead-media lead-media-image">
                <button type="button" className="lead-media-preview-button" onClick={() => onPreviewImage({ url, name })}>
                    <img src={url} alt={name} />
                </button>
                <a href={url} download={name} target="_blank" rel="noreferrer" title="Скачать">
                    <DownloadOutlined />
                </a>
            </div>
        )
    }

    if (isVideo) {
        return (
            <div className="lead-media lead-media-video">
                <video src={url} controls />
                <a href={url} download={name} target="_blank" rel="noreferrer" title="Скачать">
                    <DownloadOutlined />
                </a>
            </div>
        )
    }

    if (isAudio) {
        return (
            <div className="lead-media lead-media-audio">
                <audio src={url} controls />
                <a href={url} download={name} target="_blank" rel="noreferrer" title="Скачать">
                    <DownloadOutlined />
                </a>
            </div>
        )
    }

    if (!url && !item.media_file_name) {
        return <Text type="secondary">Медиа-сообщение</Text>
    }

    return (
        <a className="lead-media-file" href={url || undefined} download={name} target="_blank" rel="noreferrer">
            <FileOutlined />
            <span>
                <strong>{name}</strong>
                <small>{[contentType || 'файл', size].filter(Boolean).join(' · ')}</small>
            </span>
            {url && <DownloadOutlined />}
        </a>
    )
}

function DeliveryStatus({ item }: { item: LeadMessage }) {
    if (!item.outgoing) return null
    const isRead = item.is_read || item.delivery_status === 'read'
    return (
        <span className={`lead-message-status ${isRead ? 'is-read' : ''}`}>
            {isRead ? <CheckCircleOutlined /> : <CheckOutlined />}
            {isRead ? 'Прочитано' : 'Отправлено'}
        </span>
    )
}

export default function LeadChats() {
    const navigate = useNavigate()
    const { user, profileLoaded } = useAuthStore()
    const [searchParams] = useSearchParams()
    const [geos, setGeos] = useState<GeoFolder[]>([])
    const [selectedGeo, setSelectedGeo] = useState<string>(ALL_GEOS_VALUE)
    const [leadSort, setLeadSort] = useState<'activity' | 'unread' | 'name'>('activity')
    // Список чатов приходит из рабочего пространства: там есть и диалоги без
    // карточки лида (написали сами), которых нет в списке лидов.
    const [workTab, setWorkTab] = useState<WorkTab>('active')
    const [workItems, setWorkItems] = useState<ChatWorkItem[]>([])
    // Подгрузка списка порциями: первая отрисовка быстрее, а длинный список
    // больше не обрезается молча на трёхстах строках.
    const PAGE_SIZE = 100
    const [hasMoreWork, setHasMoreWork] = useState(false)
    const [loadingMoreWork, setLoadingMoreWork] = useState(false)
    // Поисковая строка уходит на сервер с задержкой: иначе запрос улетал бы на
    // каждую букву.
    const [debouncedSearch, setDebouncedSearch] = useState('')
    const [folderUnread, setFolderUnread] = useState<Record<number, number>>({})
    const [workCounts, setWorkCounts] = useState<Record<string, number>>({})
    const [loadingWork, setLoadingWork] = useState(false)
    const [selectedItem, setSelectedItem] = useState<ChatWorkItem | null>(null)
    const [actionLoading, setActionLoading] = useState(false)
    const [cardPanelOpen, setCardPanelOpen] = useState(false)
    // Диалог без карточки лида: своя история и своё поле ответа, потому что
    // обычная панель завязана на лида.
    const [draftOpen, setDraftOpen] = useState(false)
    const [draft, setDraft] = useState<LeadDraft | null>(null)
    const [draftSources, setDraftSources] = useState<string[]>([])
    const [draftSaving, setDraftSaving] = useState(false)
    const [draftForm] = Form.useForm()
    const [postponeOpen, setPostponeOpen] = useState(false)
    const [postponeComment, setPostponeComment] = useState('')
    const [closeOpen, setCloseOpen] = useState(false)
    // Причина не выбрана намеренно: закрыть диалог без неё нельзя.
    const [closeOutcome, setCloseOutcome] = useState<string | undefined>(undefined)
    const [closeReasonGroups, setCloseReasonGroups] = useState<CloseReasonGroup[]>([])
    const [reopenOpen, setReopenOpen] = useState(false)
    const [reopenOutcome, setReopenOutcome] = useState<string | undefined>(undefined)
    // Окно «Изменить статус»: свободный выбор любого статуса CRM Wiseway,
    // не привязанный к закрытию/переоткрытию диалога у нас.
    const [crmStatusOpen, setCrmStatusOpen] = useState(false)
    const [crmStatusLoading, setCrmStatusLoading] = useState(false)
    const [crmStatusSaving, setCrmStatusSaving] = useState(false)
    const [crmStatusGroups, setCrmStatusGroups] = useState<CloseReasonGroup[]>([])
    const [crmStatusValue, setCrmStatusValue] = useState<string | undefined>(undefined)
    const [crmStatusCurrentLabel, setCrmStatusCurrentLabel] = useState<string | null>(null)
    const [crmStatusHasCard, setCrmStatusHasCard] = useState(true)
    // Свежесть показанных статусов CRM. Статус в шапке и в карточке - копия
    // последнего чтения их базы, и если чтение сорвалось, копия сама по себе
    // выглядит актуальной. Здесь держим отметку, чтобы честно её подписать.
    const [crmStatusStale, setCrmStatusStale] = useState<{ stale: boolean; minutes_ago: number | null } | null>(null)
    // Полная карточка лида выезжает прямо здесь: тем же окном, что и в разделе
    // «Карточки лидов», без ухода со страницы.
    const [cardDrawerLeadId, setCardDrawerLeadId] = useState<number | null>(null)
    const [reopenComment, setReopenComment] = useState('')
    const [reopenReasonGroups, setReopenReasonGroups] = useState<CloseReasonGroup[]>([])
    const [closeComment, setCloseComment] = useState('')
    // Папки чатов приватны: и сами папки, и признак их использования хранятся на
    // сервере по operator_id, поэтому настройка одинакова во всех браузерах.
    const [foldersEnabled, setFoldersEnabled] = useState(false)
    const [folders, setFolders] = useState<ChatFolder[]>([])
    const [folderAssignments, setFolderAssignments] = useState<Record<string, number>>({})
    const [activeFolderId, setActiveFolderId] = useState<number>(ALL_FOLDERS_VALUE)
    const [foldersModalOpen, setFoldersModalOpen] = useState(false)
    const [folderSaving, setFolderSaving] = useState(false)
    const [newFolderName, setNewFolderName] = useState('')
    const [newFolderColor, setNewFolderColor] = useState(FOLDER_COLOR_PRESETS[0])
    const [editingFolderId, setEditingFolderId] = useState<number | null>(null)
    const [editingFolderName, setEditingFolderName] = useState('')
    const [editingFolderColor, setEditingFolderColor] = useState(FOLDER_COLOR_PRESETS[0])
    const [leadContextMenu, setLeadContextMenu] = useState<{ x: number; y: number; chatId: number; lead: Lead | null } | null>(null)
    const [leads, setLeads] = useState<Lead[]>([])
    const [selectedLead, setSelectedLead] = useState<Lead | null>(null)
    const chatUnreadBumps = useLeadChatEventsStore((state) => state.chatUnreadBumps)
    const chatPreviews = useLeadChatEventsStore((state) => state.chatPreviews)
    const duplicateChecks = useLeadChatEventsStore((state) => state.duplicateChecks)
    const [rechecking, setRechecking] = useState(false)
    const [messagesList, setMessagesList] = useState<LeadMessage[]>([])
    const [hasEarlierMessages, setHasEarlierMessages] = useState(false)
    const [loadingEarlier, setLoadingEarlier] = useState(false)
    const skipAutoScrollRef = useRef(false)
    // Текст поля ввода - в формате Telegram (с разметкой, см. utils/tgFormat.ts).
    const [text, setText] = useState('')
    const composerRef = useRef<TgRichEditorHandle>(null)
    // Чистый текст - для проверок «пусто ли» и для сервера.
    const composerPlain = tgToPlain(text).trim()
    const [selectedFile, setSelectedFile] = useState<File | null>(null)
    const [replyTo, setReplyTo] = useState<LeadMessage | null>(null)
    // С какого аккаунта переписки ответить лиду. Выбор не меняется сам, даже
    // если лид написал на другой аккаунт: об этом говорит подсказка, а решает
    // сотрудник. null - основной аккаунт диалога.
    const [senderInfo, setSenderInfo] = useState<SenderAccounts | null>(null)
    const [senderId, setSenderId] = useState<number | null>(null)
    const [senderHintDismissed, setSenderHintDismissed] = useState<string | null>(null)
    const [senderError, setSenderError] = useState<string | null>(null)
    const [editingMessage, setEditingMessage] = useState<LeadMessage | null>(null)
    const [contextMenu, setContextMenu] = useState<{ x: number; y: number; item: LeadMessage } | null>(null)
    // Быстрые шаблоны: настройка общая на систему, поэтому её состояние
    // приходит с сервера и обновляется по WebSocket.
    const searchQuery = usePageSearchStore((state) => state.query)
    const searchOpen = usePageSearchStore((state) => state.open)
    const setSearchQuery = usePageSearchStore((state) => state.setQuery)
    const setSearchOpen = usePageSearchStore((state) => state.setOpen)

    const [templatesEnabled, setTemplatesEnabled] = useState(false)
    const [templates, setTemplates] = useState<MessageTemplate[]>([])
    const [templatesOpen, setTemplatesOpen] = useState(false)
    const [templateQuery, setTemplateQuery] = useState('')
    const [templateIndex, setTemplateIndex] = useState(0)

    const [availableReactions, setAvailableReactions] = useState<AvailableReaction[]>([])
    const [quickReactions, setQuickReactions] = useState<string[]>(readQuickReactions)
    const [reactionPickerFor, setReactionPickerFor] = useState<LeadMessage | null>(null)
    const [reactionQuery, setReactionQuery] = useState('')
    // Пока по сообщению есть незавершённый запрос, эхо от Telegram игнорируем -
    // иначе быстрые клики «поставил-снял» приводят к миганию чипа.
    const pendingReactionsRef = useRef<Set<number>>(new Set())
    const [imagePreview, setImagePreview] = useState<ImagePreviewState | null>(null)
    const [showScrollBottom, setShowScrollBottom] = useState(false)
    const [highlightedMessageId, setHighlightedMessageId] = useState<number | null>(null)
    const [pinnedJumpIndex, setPinnedJumpIndex] = useState(0)
    const [emojiOpen, setEmojiOpen] = useState(false)
    const [loadingGeos, setLoadingGeos] = useState(false)
    const [loadingLeads, setLoadingLeads] = useState(false)
    const [loadingMessages, setLoadingMessages] = useState(false)
    const [sending, setSending] = useState(false)
    const fileInputRef = useRef<HTMLInputElement>(null)
    const messagesContainerRef = useRef<HTMLDivElement>(null)
    const messagesEndRef = useRef<HTMLDivElement>(null)

    const [statuses, setStatuses] = useState<{ key: string; label: string; substatuses: string[] }[]>([])
    const requestedGeo = searchParams.get('geo') || undefined
    const requestedLeadId = searchParams.get('leadId')

    const selectedGeoInfo = useMemo(
        () => geos.find((geo) => geo.name === selectedGeo),
        [geos, selectedGeo]
    )
    const allGeoAssignedCount = useMemo(() => geos.reduce((sum, geo) => sum + geo.assigned_count, 0), [geos])
    const allGeoAvailableCount = useMemo(() => geos.reduce((sum, geo) => sum + geo.available_count, 0), [geos])
    const isAllGeosSelected = selectedGeo === ALL_GEOS_VALUE

    // Сортировка списка лидов только для отображения: массив leads и его
    // обновления (загрузка, WebSocket-счётчики) не трогаются.
    const sortedLeads = useMemo(() => {
        const leadTime = (lead: Lead) => {
            const value = lead.last_message_at || lead.updated_at
            return value ? new Date(value).getTime() : 0
        }
        const byActivity = (a: Lead, b: Lead) => leadTime(b) - leadTime(a)
        const arr = [...leads]
        if (leadSort === 'unread') {
            arr.sort((a, b) => (b.unread_count || 0) - (a.unread_count || 0) || byActivity(a, b))
        } else if (leadSort === 'name') {
            arr.sort((a, b) => leadName(a).localeCompare(leadName(b)) || byActivity(a, b))
        } else {
            arr.sort(byActivity)
        }
        return arr
    }, [leads, leadSort])
    const pinnedMessages = useMemo(() => messagesList.filter((item) => item.is_pinned), [messagesList])
    const pinnedJumpMessages = useMemo(() => [...pinnedMessages].reverse(), [pinnedMessages])
    const currentPinnedMessage = pinnedJumpMessages.length ? pinnedJumpMessages[pinnedJumpIndex % pinnedJumpMessages.length] : null

    const loadGeos = useCallback(async () => {
        setLoadingGeos(true)
        try {
            const [geoResponse, statusResponse] = await Promise.all([leadsApi.getGeos(), leadsApi.getStatuses()])
            setGeos(geoResponse.data)
            setStatuses(statusResponse.data)
            setSelectedGeo((current) => requestedGeo || current || ALL_GEOS_VALUE)
        } catch (error) {
            message.error(extractError(error))
        } finally {
            setLoadingGeos(false)
        }
    }, [requestedGeo])

    const loadLeads = useCallback(async () => {
        setLoadingLeads(true)
        try {
            const response = await leadsApi.getLeads({ geo: isAllGeosSelected ? undefined : selectedGeo, scope: 'assigned', limit: 200 })
            setLeads(response.data)
            setSelectedLead((current) => {
                const targetId = requestedLeadId ? Number(requestedLeadId) : 0
                if (targetId) {
                    const target = response.data.find((lead) => lead.id === targetId)
                    if (target) return target
                }
                if (current && response.data.some((lead) => lead.id === current.id)) {
                    return response.data.find((lead) => lead.id === current.id) || current
                }
                // Сами ничего не открываем: раздел встречает пустым экраном, пока
                // оператор не выберет диалог. Иначе сюда подставлялся первый лид
                // из списка закреплённых - в том числе давно закрытый.
                // Но уже открытый диалог не сбрасываем: в этом списке только
                // закреплённые за тобой лиды, и раньше после отправки сообщения
                // или по прямой ссылке чужой или ничейный диалог закрывался сам.
                return current
            })
        } catch (error) {
            message.error(extractError(error))
        } finally {
            setLoadingLeads(false)
        }
    }, [selectedGeo, isAllGeosSelected, requestedLeadId])

    const loadMessages = useCallback(async () => {
        if (!selectedLead) {
            setMessagesList([])
            return
        }
        setLoadingMessages(true)
        try {
            const response = await leadsApi.getMessages(selectedLead.id, { limit: 100 })
            setMessagesList(response.data)
            setHasEarlierMessages(response.data.length >= 100)
            if (!selectedLead.telegram_avatar_url) {
                const leadResponse = await leadsApi.getLead(selectedLead.id)
                setSelectedLead((current) => current?.id === leadResponse.data.id && current.telegram_avatar_url !== leadResponse.data.telegram_avatar_url ? leadResponse.data : current)
                setLeads((items) => items.map((item) => item.id === leadResponse.data.id ? leadResponse.data : item))
            }
        } catch (error) {
            message.error(extractError(error))
        } finally {
            setLoadingMessages(false)
        }
    }, [selectedLead])

    // Список аккаунтов переписки обновляем при смене лида и когда приходит
    // новое сообщение от него - он мог написать на другой наш аккаунт.
    const selectedLeadId = selectedLead?.id ?? null
    const lastIncomingMessageId = useMemo(() => {
        for (let index = messagesList.length - 1; index >= 0; index -= 1) {
            if (!messagesList[index].outgoing) return messagesList[index].id
        }
        return null
    }, [messagesList])
    const senderLeadRef = useRef<number | null>(null)
    useEffect(() => {
        if (!selectedLeadId) {
            setSenderInfo(null)
            setSenderId(null)
            senderLeadRef.current = null
            return
        }
        const leadChanged = senderLeadRef.current !== selectedLeadId
        senderLeadRef.current = selectedLeadId
        if (leadChanged) {
            setSenderInfo(null)
            setSenderId(null)
            setSenderError(null)
        }
        let cancelled = false
        leadsApi.getSenderAccounts(selectedLeadId)
            .then(({ data }) => {
                if (cancelled) return
                setSenderInfo(data)
                // Выбранный аккаунт мог выпасть из списка или стать недоступным -
                // тогда возвращаемся к основному.
                setSenderId((current) => {
                    const chosen = data.accounts.find((account) => account.id === current)
                    return chosen && chosen.state !== 'unusable' ? current : (data.main_account_id ?? null)
                })
            })
            .catch(() => { if (!cancelled) setSenderInfo(null) })
        return () => { cancelled = true }
    }, [selectedLeadId, lastIncomingMessageId])

    useEffect(() => {
        loadGeos()
    }, [loadGeos])

    useEffect(() => {
        loadLeads()
    }, [loadLeads])

    useEffect(() => {
        loadMessages()
    }, [loadMessages])

    useEffect(() => {
        chatFoldersApi.getSettings()
            .then((response) => setFoldersEnabled(Boolean(response.data.enabled)))
            .catch(() => setFoldersEnabled(false))
    }, [])

    const loadFolders = useCallback(async () => {
        if (!foldersEnabled) return
        try {
            const [foldersResponse, assignmentsResponse] = await Promise.all([
                chatFoldersApi.getFolders(),
                chatFoldersApi.getAssignments(),
            ])
            setFolders(foldersResponse.data)
            setFolderAssignments(assignmentsResponse.data.assignments || {})
        } catch (error) {
            message.error(extractError(error))
        }
    }, [foldersEnabled])

    useEffect(() => {
        loadFolders()
    }, [loadFolders])

    const toggleFolders = async (enabled: boolean) => {
        const previous = foldersEnabled
        setFoldersEnabled(enabled)
        if (!enabled) setActiveFolderId(ALL_FOLDERS_VALUE)
        try {
            await chatFoldersApi.updateSettings(enabled)
        } catch (error) {
            setFoldersEnabled(previous)
            message.error(extractError(error))
        }
    }

    const createFolder = async () => {
        const name = newFolderName.trim()
        if (!name) return
        setFolderSaving(true)
        try {
            await chatFoldersApi.createFolder({ name, color: newFolderColor })
            setNewFolderName('')
            setNewFolderColor(FOLDER_COLOR_PRESETS[0])
            await loadFolders()
            message.success('Папка создана')
        } catch (error) {
            message.error(extractError(error))
        } finally {
            setFolderSaving(false)
        }
    }

    const saveFolderEdit = async () => {
        if (editingFolderId === null) return
        const name = editingFolderName.trim()
        if (!name) return
        setFolderSaving(true)
        try {
            await chatFoldersApi.updateFolder(editingFolderId, { name, color: editingFolderColor })
            setEditingFolderId(null)
            await loadFolders()
            message.success('Папка обновлена')
        } catch (error) {
            message.error(extractError(error))
        } finally {
            setFolderSaving(false)
        }
    }

    const removeFolder = async (folder: ChatFolder) => {
        try {
            await chatFoldersApi.deleteFolder(folder.id)
            if (activeFolderId === folder.id) setActiveFolderId(ALL_FOLDERS_VALUE)
            await loadFolders()
            message.success('Папка удалена')
        } catch (error) {
            message.error(extractError(error))
        }
    }

    // Пункт меню «Удалить чат»: убирает диалог из списка. В Telegram переписка
    // остаётся, у нас сохраняется история - убирается только строка, чтобы
    // служебные и брошенные диалоги не мешали работать.
    const removeChatFromList = (chatId: number) => {
        setLeadContextMenu(null)
        Modal.confirm({
            title: 'Удалить чат из списка?',
            content: 'Переписка в Telegram и история в Оркестраторе останутся. Диалог просто пропадёт из списка.',
            okText: 'Удалить',
            cancelText: 'Отмена',
            okButtonProps: { danger: true },
            onOk: async () => {
                try {
                    await chatsApi.archive(chatId)
                    setWorkItems((items) => items.filter((item) => item.chat_id !== chatId))
                    setSelectedItem((current) => current && current.chat_id === chatId ? null : current)
                    message.success('Чат удалён из списка')
                    await loadWorkItems()
                } catch (error) {
                    message.error(extractError(error))
                }
            },
        })
    }

    // Пункт меню «Отметить прочитанным»: работает при любых условиях, включая
    // включённый тумблер «прочитано только после ответа» - это осознанное
    // действие человека, а не автоматика.
    const markChatReadForced = async (chatId: number, leadId: number | null) => {
        setLeadContextMenu(null)
        try {
            await chatsApi.markAsRead(chatId, true)
            setWorkItems((items) => items.map((item) => item.chat_id === chatId ? { ...item, unread_count: 0 } : item))
            setSelectedItem((current) => current && current.chat_id === chatId ? { ...current, unread_count: 0 } : current)
            if (leadId) {
                setLeads((items) => items.map((item) => item.id === leadId ? { ...item, unread_count: 0 } : item))
                setSelectedLead((current) => current && current.id === leadId ? { ...current, unread_count: 0 } : current)
            }
        } catch (error) {
            message.error(extractError(error))
        }
    }

    const assignLeadToFolder = async (lead: Lead, folderId: number | null) => {
        setLeadContextMenu(null)
        try {
            await chatFoldersApi.assignLead(lead.id, folderId)
            await loadFolders()
        } catch (error) {
            message.error(extractError(error))
        }
    }

    const folderById = useMemo(() => new Map(folders.map((folder) => [folder.id, folder])), [folders])
    const leadFolder = useCallback(
        (lead: Lead) => folderById.get(folderAssignments[String(lead.id)]) || null,
        [folderById, folderAssignments]
    )

    // Фильтрацию, поиск и сортировку делает сервер: список может быть длиннее
    // загруженной порции, и отбирать по ней было бы неверно. Пока идёт запрос
    // с новой строкой поиска, показываем то, что уже есть, - чтобы список не
    // моргал на каждую букву.
    const visibleWorkItems = workItems

    const visibleLeads = useMemo(() => {
        if (!foldersEnabled || activeFolderId === ALL_FOLDERS_VALUE) return sortedLeads
        return sortedLeads.filter((lead) => folderAssignments[String(lead.id)] === activeFolderId)
    }, [sortedLeads, foldersEnabled, activeFolderId, folderAssignments])

    // Что сейчас показываем: вкладка, гео, папка, поиск и сортировка. Всё это
    // считает сервер, поэтому фильтр видит всю базу, а не загруженную пачку.
    const workQuery = useMemo(() => ({
        tab: workTab,
        search: debouncedSearch.trim() || undefined,
        geo: isAllGeosSelected ? undefined : selectedGeo,
        folder_id: foldersEnabled && activeFolderId !== ALL_FOLDERS_VALUE ? activeFolderId : undefined,
        sort: leadSort,
    }), [workTab, debouncedSearch, isAllGeosSelected, selectedGeo, foldersEnabled, activeFolderId, leadSort])

    const loadWorkItems = useCallback(async () => {
        setLoadingWork(true)
        try {
            const [itemsResponse, countsResponse] = await Promise.all([
                chatWorkspaceApi.getChats({ ...workQuery, limit: PAGE_SIZE }),
                chatWorkspaceApi.getCounts({ geo: workQuery.geo, folder_id: workQuery.folder_id }),
            ])
            setWorkItems(itemsResponse.data)
            setHasMoreWork(itemsResponse.data.length >= PAGE_SIZE)
            setWorkCounts(countsResponse.data || {})
        } catch (error) {
            message.error(extractError(error))
        } finally {
            setLoadingWork(false)
        }
    }, [workQuery])

    // Тихое обновление списка по событию: новый собеседник или диалог, которого
    // в списке ещё нет. Берём первую страницу и ставим её сверху, уже
    // подгруженные ниже строки оставляем - прокрутка и выделение не сбиваются.
    const refreshWorkItemsSilently = useCallback(async () => {
        try {
            const [itemsResponse, countsResponse] = await Promise.all([
                chatWorkspaceApi.getChats({ ...workQuery, limit: PAGE_SIZE }),
                chatWorkspaceApi.getCounts({ geo: workQuery.geo, folder_id: workQuery.folder_id }),
            ])
            const fresh = itemsResponse.data
            const freshIds = new Set(fresh.map((item) => item.chat_id))
            setWorkItems((items) => [...fresh, ...items.filter((item) => !freshIds.has(item.chat_id))])
            setWorkCounts(countsResponse.data || {})
        } catch {
            // Фоновое обновление: при сбое список остаётся как был.
        }
    }, [workQuery])

    // Следующая порция. По активности листаем курсором - список живой, и
    // смещение при новом сообщении теряло бы строки. Остальные сортировки
    // листаются смещением: там значения меняются на ходу и курсора нет.
    const loadMoreWorkItems = useCallback(async () => {
        if (loadingMoreWork || !hasMoreWork || !workItems.length) return
        setLoadingMoreWork(true)
        try {
            const last = workItems[workItems.length - 1]
            const response = await chatWorkspaceApi.getChats({
                ...workQuery,
                limit: PAGE_SIZE,
                ...(workQuery.sort === 'activity' && last.last_message_at
                    ? { cursor_at: last.last_message_at, cursor_id: last.chat_id }
                    : { offset: workItems.length }),
            })
            const known = new Set(workItems.map((item) => item.chat_id))
            const fresh = response.data.filter((item) => !known.has(item.chat_id))
            setWorkItems((items) => [...items, ...fresh])
            setHasMoreWork(response.data.length >= PAGE_SIZE)
        } catch (error) {
            message.error(extractError(error))
        } finally {
            setLoadingMoreWork(false)
        }
    }, [loadingMoreWork, hasMoreWork, workItems, workQuery])

    useEffect(() => {
        loadWorkItems()
    }, [loadWorkItems])

    // Задержка перед отправкой поиска на сервер.
    useEffect(() => {
        const timer = window.setTimeout(() => setDebouncedSearch(searchQuery), 300)
        return () => window.clearTimeout(timer)
    }, [searchQuery])

    // Непрочитанные по папкам считает база - цифра верна независимо от того,
    // сколько строк подгружено.
    useEffect(() => {
        if (!foldersEnabled || user?.folder_unread_badges !== true) {
            setFolderUnread({})
            return
        }
        let cancelled = false
        chatWorkspaceApi.getFolderUnread({ tab: workTab, geo: workQuery.geo })
            .then((response) => {
                if (cancelled) return
                const totals: Record<number, number> = {}
                for (const [folderId, count] of Object.entries(response.data || {})) {
                    totals[Number(folderId)] = Number(count)
                }
                setFolderUnread(totals)
            })
            .catch(() => undefined)
        return () => { cancelled = true }
    }, [foldersEnabled, user?.folder_unread_badges, workTab, workQuery.geo, workItems])

    // Выбор диалога: для лида подтягиваем карточку, чтобы работала вся правая
    // панель; диалог без лида открывается в своём окне переписки.
    const selectWorkItem = useCallback(async (item: ChatWorkItem) => {
        setSelectedItem(item)
        if (!item.lead_id) {
            setSelectedLead(null)
            setMessagesList([])
            return
        }
        try {
            const response = await leadsApi.getLead(item.lead_id)
            setSelectedLead(response.data)
        } catch {
            setSelectedLead(null)
        }
    }, [])

    // Своя ссылка у каждого диалога: /chats/<номер диалога>. Адрес и открытый
    // диалог держатся в согласии в обе стороны: клик по чату меняет адрес, а
    // адрес (прямая ссылка, «назад» в браузере) открывает нужный чат.
    const { chatId: routeChatParam } = useParams()
    const navigationType = useNavigationType()
    const routeChatId = Number(routeChatParam) || 0
    const legacyChatId = Number(searchParams.get('chat')) || 0
    // Что сейчас открываем по адресу: чтобы не запрашивать дважды и не снять
    // адрес, пока диалог ещё грузится.
    const openingChatRef = useRef<number | null>(null)

    const openChatFromUrl = useCallback(async (load: () => Promise<{ data: ChatWorkItem }>, key: number) => {
        openingChatRef.current = key
        try {
            const { data } = await load()
            const tab = workTabOf(data)
            setWorkTab((current) => (current === tab ? current : tab))
            await selectWorkItem(data)
            return data
        } catch (error) {
            message.error(extractError(error))
            return null
        } finally {
            openingChatRef.current = null
        }
    }, [selectWorkItem])

    useEffect(() => {
        if (routeChatId) {
            if (selectedItem?.chat_id === routeChatId || openingChatRef.current === routeChatId) return
            void openChatFromUrl(() => chatWorkspaceApi.getItem(routeChatId), routeChatId).then((item) => {
                if (!item) navigate('/chats', { replace: true })
            })
            return
        }
        // Старые ссылки: ?chat= ставят уведомления о диалогах без лида, ?leadId= -
        // уведомления, задачи и карточки лидов. Переводим их на новый адрес.
        if (legacyChatId) {
            navigate(`/chats/${legacyChatId}`, { replace: true })
            return
        }
        const leadId = Number(requestedLeadId) || 0
        if (leadId) {
            if (openingChatRef.current === -leadId) return
            void openChatFromUrl(() => chatWorkspaceApi.getItemByLead(leadId), -leadId).then((item) => {
                // Не нашёлся - остаётся прежнее поведение: лида откроет загрузка списка.
                if (item) navigate(`/chats/${item.chat_id}`, { replace: true })
            })
            return
        }
        // «Назад» до /chats без диалога - закрываем открытый, как было до него.
        if (navigationType === 'POP' && selectedItem) {
            setSelectedItem(null)
            setSelectedLead(null)
        }
    // Срабатываем только на смену адреса; остальное читаем как есть.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [routeChatId, legacyChatId, requestedLeadId])

    useEffect(() => {
        // Пока диалог открывается по адресу, адресом занимается само открытие.
        // Иначе старая ссылка ?leadId= оставляла лишнюю запись в истории, и
        // «назад» снова и снова открывал тот же чат.
        if (openingChatRef.current !== null) return
        const chatId = selectedItem?.chat_id || 0
        if (chatId && chatId !== routeChatId) {
            // Каждый открытый диалог - отдельная запись в истории: «назад»
            // возвращает к предыдущему чату.
            navigate(`/chats/${chatId}`)
        } else if (!chatId && routeChatId) {
            navigate('/chats', { replace: true })
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedItem?.chat_id])

    const openLeadDraft = async () => {
        if (!selectedItem) return
        try {
            const [response, dictionaries] = await Promise.all([
                chatWorkspaceApi.getLeadDraft(selectedItem.chat_id),
                leadsApi.getCrmDictionaries().catch(() => null),
            ])
            setDraft(response.data)
            setDraftSources(dictionaries?.data.sources || [])
            draftForm.setFieldsValue({
                // Название лида - его TG username. Нет username - вводится вручную.
                title: response.data.title || '',
                telegram_nickname: response.data.telegram_nickname || '',
                source: response.data.source || undefined,
                phone: response.data.phone || '',
                first_name: response.data.first_name || undefined,
                last_name: response.data.last_name || undefined,
                geo: undefined,
                temperature: DEFAULT_TEMPERATURE,
            })
            setDraftOpen(true)
        } catch (error) {
            message.error(extractError(error))
        }
    }

    // После «Добавить лида» и привязки к карточке диалог уже с лидом и, как
    // правило, в другой вкладке (у нажавшего в «Активных»). Открываем его
    // сразу в новом виде - иначе справа оставался диалог «без карточки» с
    // кнопкой добавления, пока не обновишь страницу.
    const openUpdatedItem = async (item: ChatWorkItem) => {
        const tab = workTabOf(item)
        const sameTab = tab === workTab
        if (!sameTab) setWorkTab(tab)
        await selectWorkItem(item)
        if (sameTab) await loadWorkItems()
    }

    const showWarnings = (warnings?: string[] | string | null) => {
        const list = Array.isArray(warnings) ? warnings : warnings ? [warnings] : []
        list.forEach((text) => message.warning(text, 6))
    }

    // Дубль нашёлся уже после открытия окна (второй нажал раньше) - окно
    // перечитываем: вместо «Создать» будет «Объединить с карточкой».
    const reloadDraft = async () => {
        if (!selectedItem) return
        try {
            const response = await chatWorkspaceApi.getLeadDraft(selectedItem.chat_id)
            setDraft(response.data)
            replaceWorkItem({ ...selectedItem, duplicate: response.data.duplicate })
        } catch (error) {
            message.error(extractError(error))
        }
    }

    const createLeadFromChat = async () => {
        if (!selectedItem) return
        try {
            const values = await draftForm.validateFields()
            setDraftSaving(true)
            const response = await chatWorkspaceApi.createLeadFromChat(selectedItem.chat_id, values)
            message.success(response.data.result_message || 'Карточка создана')
            showWarnings(response.data.crm_warning)
            setDraftOpen(false)
            await openUpdatedItem(response.data)
        } catch (error) {
            if (error && typeof error === 'object' && 'errorFields' in error) return
            const status = (error as { response?: { status?: number } })?.response?.status
            message.error(extractError(error))
            if (status === 409) await reloadDraft()
        } finally {
            setDraftSaving(false)
        }
    }

    const mergeLeadFromChat = async () => {
        if (!selectedItem) return
        const chatId = selectedItem.chat_id
        setDraftSaving(true)
        try {
            const { data } = await chatWorkspaceApi.mergeLeadFromChat(chatId)
            if (data.fraud) message.warning(data.message, 6)
            else message.success(data.message, 5)
            showWarnings(data.warnings)
            setDraftOpen(false)
            if (data.item) {
                await openUpdatedItem(data.item)
            } else {
                // Лид ушёл другому ответственному - диалог нажавшему больше не виден.
                setWorkItems((items) => items.filter((item) => item.chat_id !== chatId))
                setSelectedItem((current) => current && current.chat_id === chatId ? null : current)
                await loadWorkItems()
            }
        } catch (error) {
            message.error(extractError(error))
            const status = (error as { response?: { status?: number } })?.response?.status
            if (status === 409) await reloadDraft()
        } finally {
            setDraftSaving(false)
        }
    }

    // Недоступный аккаунт диалога: причина и кем его можно заменить.
    // Подмену делаем только по нажатию - для лида это смена собеседника.
    const [accountIssue, setAccountIssue] = useState<{
        reason: string | null
        current_account: { id: number | null; username: string | null; phone: string | null }
        replacement: { id: number; username: string | null; phone: string | null } | null
    } | null>(null)
    const [switchingAccount, setSwitchingAccount] = useState(false)
    // Красное предупреждение в чате можно закрыть крестиком и вернуть нажатием
    // на красный кружок. В новом диалоге оно снова показано.
    const [accountAlertHidden, setAccountAlertHidden] = useState(false)

    // Передача лида другому сотруднику: список сотрудников грузим при первом
    // открытии окна, а не на каждый чат.
    const [transferOpen, setTransferOpen] = useState(false)
    const [transferTo, setTransferTo] = useState<number | undefined>(undefined)
    const [staff, setStaff] = useState<Array<{ id: number; username: string; full_name?: string | null }>>([])

    const openTransfer = async () => {
        setTransferTo(undefined)
        setTransferOpen(true)
        if (staff.length) return
        try {
            const response = await operatorsApi.getAll(0, 200)
            setStaff(response.data as Array<{ id: number; username: string; full_name?: string | null }>)
        } catch (error) {
            message.error(extractError(error))
        }
    }

    const runChatAction = async (
        action: () => Promise<unknown>,
        successText: string,
        options?: { keepOpen?: boolean; nextState?: string; goToTab?: WorkTab },
    ) => {
        setActionLoading(true)
        try {
            const response = await action()
            message.success(successText)

            // Закрытие уже состоялось, но статус мог не уйти в Wiseway -
            // об этом оператор должен узнать сразу, а не из логов.
            const warning = (response as { data?: { crm_warning?: string | null } })?.data?.crm_warning
            if (warning) message.warning(warning, 6)

            if (options?.keepOpen) {
                // «Взять в работу» нажимают, чтобы продолжить переписку в этом
                // же диалоге, поэтому его не закрываем. Состояние правим на
                // месте - кнопки в шапке должны пересобраться сразу, не
                // дожидаясь ответа сервера со списком.
                if (options.nextState) {
                    const nextState = options.nextState
                    setSelectedItem((current) => (current ? { ...current, work_state: nextState } : current))
                    setSelectedLead((current) => (current ? { ...current, work_state: nextState } : current))
                }
                // Диалог переезжает в другую вкладку - переключаем её, иначе
                // открытый чат пропал бы из списка слева.
                if (options.goToTab && options.goToTab !== workTab) {
                    setWorkTab(options.goToTab)
                } else {
                    await loadWorkItems()
                }
            } else {
                await loadWorkItems()
                setSelectedItem(null)
                setSelectedLead(null)
            }
        } catch (error) {
            message.error(extractError(error))
        } finally {
            setActionLoading(false)
        }
    }

    const openCrmStatus = async () => {
        if (!activeChatId) return
        setCrmStatusOpen(true)
        setCrmStatusLoading(true)
        setCrmStatusValue(undefined)
        try {
            const { data } = await chatWorkspaceApi.getCrmStatusOptions(activeChatId)
            setCrmStatusGroups(data.groups || [])
            setCrmStatusValue(data.current_status || undefined)
            setCrmStatusCurrentLabel(data.current_label)
            setCrmStatusHasCard(data.has_crm_card)
        } catch (error) {
            message.error(extractError(error))
            setCrmStatusOpen(false)
        } finally {
            setCrmStatusLoading(false)
        }
    }

    const saveCrmStatus = async () => {
        if (!activeChatId || !crmStatusValue) return
        setCrmStatusSaving(true)
        try {
            const response = await chatWorkspaceApi.setCrmStatus(activeChatId, crmStatusValue)
            const warning = response.data.crm_warning
            if (warning) {
                // Статус не ушёл в их базу - окно оставляем открытым, чтобы
                // можно было тут же попробовать снова или выбрать другой статус.
                message.warning(warning, 6)
            } else {
                message.success('Статус передан в CRM Wiseway')
                setCrmStatusOpen(false)
                // Подсказка «смените статус» больше не актуальна.
                setSelectedItem((current) => (current && current.chat_id === activeChatId
                    ? { ...current, needs_status_update: false, status_hint_label: null }
                    : current))
                setWorkItems((items) => items.map((item) => (item.chat_id === activeChatId
                    ? { ...item, needs_status_update: false, status_hint_label: null }
                    : item)))
            }
        } catch (error) {
            message.error(extractError(error))
        } finally {
            setCrmStatusSaving(false)
        }
    }

    // Кнопка «Обновить» выводится порталом в верхнюю панель приложения.
    const [headerSlot, setHeaderSlot] = useState<HTMLElement | null>(null)
    useEffect(() => {
        setHeaderSlot(document.getElementById('chat-header-slot'))
    }, [])

    // Диалог может быть выбран как из списка, так и по ссылке на лида -
    // действия и статус должны работать в обоих случаях.
    const activeChatId = selectedItem?.chat_id ?? selectedLead?.chat_id ?? null
    const activeWorkState = selectedItem?.work_state ?? selectedLead?.work_state ?? 'none'

    // Спрашиваем сервер о состоянии аккаунта только когда индикатор здоровья
    // уже говорит о проблеме - иначе это лишний запрос на каждый чат.
    const accountLooksBroken = Boolean(selectedItem?.account_issue)
        || accountProblemStatuses.includes(
            selectedItem?.account_health_status || selectedLead?.account_health_status || ''
        )
    useEffect(() => {
        setAccountAlertHidden(false)
    }, [activeChatId])

    useEffect(() => {
        if (!activeChatId || !accountLooksBroken) {
            setAccountIssue(null)
            return
        }
        let cancelled = false
        chatWorkspaceApi.getAccountReplacement(activeChatId)
            .then((response) => { if (!cancelled) setAccountIssue(response.data) })
            .catch(() => { if (!cancelled) setAccountIssue(null) })
        return () => { cancelled = true }
    }, [activeChatId, accountLooksBroken])

    const switchChatAccount = async () => {
        if (!activeChatId || !accountIssue?.replacement) return
        setSwitchingAccount(true)
        try {
            const response = await chatWorkspaceApi.switchAccount(activeChatId, accountIssue.replacement.id)
            setSelectedItem(response.data)
            setAccountIssue(null)
            message.success('Переписка продолжится с нового аккаунта')
            await loadWorkItems()
        } catch (error) {
            message.error(extractError(error))
        } finally {
            setSwitchingAccount(false)
        }
    }

    // Состояние аккаунта диалога для кружка внизу. Сервер может сообщить о
    // проблеме и при «здоровом» статусе (аккаунт просто не на связи) - это
    // проходит само, поэтому жёлтый, а не зелёный.
    // Последнее слово за проверкой сервера: он сверяет срок блока с текущим
    // временем. Сохранённый статус может отставать - истёкший временный блок
    // числится в нём, пока аккаунт снова не попробует отправить сообщение.
    const storedHealth = accountHealth(selectedItem, selectedLead)
    const serverSaysOk = accountIssue !== null && !accountIssue.reason
    const composerHealth = serverSaysOk
        ? { label: 'Аккаунт работает', color: 'var(--success)', kind: 'ok' as AccountHealthKind }
        : storedHealth
    const accountKind: AccountHealthKind = composerHealth.kind === 'ok' && accountIssue?.reason
        ? 'temporary'
        : composerHealth.kind
    const accountDotColor = accountKind === 'ok'
        ? 'var(--success)'
        : accountKind === 'temporary' ? 'var(--warning)' : 'var(--error)'
    const accountStateLabel = accountKind === 'temporary' && composerHealth.kind === 'ok'
        ? 'Аккаунт сейчас не на связи'
        : composerHealth.label

    // Текст о проблеме один и тот же: и в красном предупреждении в чате, и в
    // окошке по нажатию на жёлтый кружок.
    const accountName = (account: { username: string | null; phone: string | null } | null | undefined) =>
        account ? (account.username ? '@' + account.username : (account.phone || '')) : ''
    const accountIssueTitle = accountIssue?.reason
        ? `Аккаунт ${accountName(accountIssue.current_account)} недоступен: ${accountIssue.reason}`
        : accountStateLabel
    const accountIssueDescription = accountIssue?.reason
        ? (accountIssue.replacement
            ? `Переписку можно продолжить с аккаунта ${accountName(accountIssue.replacement)}. Лид увидит сообщение от другого собеседника.`
            : 'Подходящей замены в пуле сейчас нет — обратитесь к администратору.')
        : undefined
    const accountIssueAction = accountIssue?.replacement ? (
        <Button size="small" type="primary" loading={switchingAccount} onClick={switchChatAccount}>
            Продолжить с {accountName(accountIssue.replacement)}
        </Button>
    ) : undefined

    // Заметки грузятся только при открытой карточке: переключение чатов в списке
    // не должно дёргать сервер лишний раз.
    const [notes, setNotes] = useState<LeadNote[]>([])
    const [notesLoading, setNotesLoading] = useState(false)
    const [noteDraft, setNoteDraft] = useState('')
    const [noteRemindAt, setNoteRemindAt] = useState('')
    const [noteSaving, setNoteSaving] = useState(false)
    // reminderNoteId === null означает, что срок выбирается для новой заметки.
    const [reminderOpen, setReminderOpen] = useState(false)
    const [reminderNoteId, setReminderNoteId] = useState<number | null>(null)
    const [reminderValue, setReminderValue] = useState<Dayjs | null>(null)

    const noteLeadId = selectedLead?.id ?? null

    const loadNotes = useCallback(async (leadId: number | null, chatId: number | null) => {
        if (!leadId && !chatId) {
            setNotes([])
            return
        }
        setNotesLoading(true)
        try {
            const response = await leadNotesApi.getNotes({ leadId, chatId })
            setNotes(response.data)
        } catch {
            setNotes([])
        } finally {
            setNotesLoading(false)
        }
    }, [])

    useEffect(() => {
        if (!cardPanelOpen) return
        setNoteDraft('')
        setNoteRemindAt('')
        loadNotes(noteLeadId, activeChatId)
    }, [cardPanelOpen, noteLeadId, activeChatId, loadNotes])

    // Порядок: просроченные требуют действия и идут первыми, за ними ближайшие
    // напоминания, в конце обычные заметки от новых к старым.
    const sortedNotes = useMemo(() => {
        const now = Date.now()
        const group = (note: LeadNote) => {
            if (!note.remind_at) return 2
            return new Date(note.remind_at).getTime() <= now ? 0 : 1
        }
        return [...notes].sort((a, b) => {
            const groupDiff = group(a) - group(b)
            if (groupDiff) return groupDiff
            if (a.remind_at && b.remind_at) {
                const diff = new Date(a.remind_at).getTime() - new Date(b.remind_at).getTime()
                return group(a) === 0 ? -diff : diff
            }
            return new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        })
    }, [notes])

    const openReminderModal = (noteId: number | null, current: string | null) => {
        setReminderNoteId(noteId)
        setReminderValue(current ? dayjs(current) : null)
        setReminderOpen(true)
    }

    const applyReminder = async (value: Dayjs | null) => {
        // Для новой заметки время просто запоминается до сохранения,
        // существующей - переносится сразу.
        if (reminderNoteId === null) {
            setNoteRemindAt(value ? value.toISOString() : '')
            setReminderOpen(false)
            return
        }
        setNoteSaving(true)
        try {
            await leadNotesApi.updateNote(reminderNoteId, value
                ? { remind_at: value.toISOString() }
                : { clear_remind: true })
            setReminderOpen(false)
            await loadNotes(noteLeadId, activeChatId)
            message.success(value ? 'Напоминание перенесено' : 'Напоминание снято')
        } catch (error) {
            message.error(extractError(error))
        } finally {
            setNoteSaving(false)
        }
    }

    const addNote = async () => {
        const text = noteDraft.trim()
        if (!text || (!noteLeadId && !activeChatId)) return
        setNoteSaving(true)
        try {
            const response = await leadNotesApi.createNote({
                lead_id: noteLeadId,
                chat_id: activeChatId,
                // datetime-local отдаёт время без зоны, dayjs переводит его в UTC для сервера.
                remind_at: noteRemindAt ? dayjs(noteRemindAt).toISOString() : null,
                text,
            })
            // Заметка сохранена у нас, но могла не уйти в ленту комментариев
            // Wiseway - об этом автор должен узнать сразу, а не из логов.
            if (response.data.crm_warning) message.warning(response.data.crm_warning, 6)
            setNoteDraft('')
            setNoteRemindAt('')
            await loadNotes(noteLeadId, activeChatId)
        } catch (error) {
            message.error(extractError(error))
        } finally {
            setNoteSaving(false)
        }
    }

    const removeNote = async (noteId: number) => {
        try {
            await leadNotesApi.deleteNote(noteId)
            setNotes((items) => items.filter((item) => item.id !== noteId))
        } catch (error) {
            message.error(extractError(error))
        }
    }

    const loadEarlierMessages = async () => {
        if (!selectedLead || loadingEarlier || loadingMessages || !messagesList.length) return
        const oldest = messagesList[0]
        setLoadingEarlier(true)
        try {
            const response = await leadsApi.getMessages(selectedLead.id, { limit: 100, before_date: oldest.date, before_id: oldest.id })
            const older = response.data
            setHasEarlierMessages(older.length >= 100)
            if (older.length) {
                const container = messagesContainerRef.current
                const previousHeight = container?.scrollHeight || 0
                skipAutoScrollRef.current = true
                setMessagesList((items) => {
                    const known = new Set(items.map((item) => item.id))
                    return [...older.filter((item) => !known.has(item.id)), ...items]
                })
                // Сохраняем видимую позицию: контент вырос сверху, сдвигаем scrollTop на разницу.
                window.setTimeout(() => {
                    if (container) container.scrollTop += container.scrollHeight - previousHeight
                }, 0)
            }
        } catch (error) {
            message.error(extractError(error))
        } finally {
            setLoadingEarlier(false)
        }
    }

    // Отчёт о прочтении в Telegram уходит при открытии диалога в любом режиме -
    // это поведение мы не меняем. Переключатель влияет только на наш счётчик.
    const markReadOnReplyOnly = user?.mark_read_on_reply_only === true
    useEffect(() => {
        const chatId = selectedLead?.chat_id
        const leadId = selectedLead?.id
        if (!chatId || !leadId || !profileLoaded) return
        chatsApi.markAsRead(chatId, !markReadOnReplyOnly)
            .then(() => {
                if (markReadOnReplyOnly) return
                setLeads((items) => items.map((item) => item.id === leadId ? { ...item, unread_count: 0 } : item))
                setSelectedLead((current) => current && current.id === leadId ? { ...current, unread_count: 0 } : current)
                setWorkItems((items) => items.map((item) => item.chat_id === chatId ? { ...item, unread_count: 0 } : item))
                setSelectedItem((current) => current && current.chat_id === chatId ? { ...current, unread_count: 0 } : current)
            })
            .catch((error: unknown) => console.error('Failed to mark lead chat as read:', error))
    }, [selectedLead?.chat_id, selectedLead?.id, markReadOnReplyOnly, profileLoaded])

    // Переход по уведомлению открывает диалог по адресу, а не кликом в списке -
    // выделение слева нужно перенести на ту же строку, иначе оно остаётся на
    // прежнем чате.
    useEffect(() => {
        const leadId = selectedLead?.id
        if (!leadId) return
        const target = workItems.find((item) => item.lead_id === leadId)
        setSelectedItem((current) => {
            if (target) return current?.chat_id === target.chat_id ? current : target
            // Диалога нет в текущей вкладке - лучше снять выделение, чем
            // подсвечивать чужую строку.
            return current?.lead_id === leadId ? current : null
        })
    }, [selectedLead?.id, workItems])

    // Обновление предпросмотра в списке: без него слева висело устаревшее
    // сообщение до перезагрузки страницы.
    const processedPreviewsRef = useRef<Record<number, number>>({})
    useEffect(() => {
        const processed = processedPreviewsRef.current
        const fresh = Object.entries(chatPreviews)
            .filter(([chatId, preview]) => preview.bump !== processed[Number(chatId)])
        if (!fresh.length) return
        processedPreviewsRef.current = Object.fromEntries(
            Object.entries(chatPreviews).map(([chatId, preview]) => [chatId, preview.bump])
        )
        setWorkItems((items) => items.map((item) => {
            const preview = fresh.find(([chatId]) => Number(chatId) === item.chat_id)?.[1]
            return preview
                ? { ...item, last_message_text: preview.text, last_message_at: preview.date }
                : item
        }))
    }, [chatPreviews])

    const processedChatBumpsRef = useRef<Record<number, number>>({})
    const workItemsRef = useRef<ChatWorkItem[]>([])
    workItemsRef.current = workItems
    const listRefreshTimerRef = useRef<number | null>(null)
    useEffect(() => () => {
        if (listRefreshTimerRef.current) window.clearTimeout(listRefreshTimerRef.current)
    }, [])
    useEffect(() => {
        const processed = processedChatBumpsRef.current
        // В режиме «прочитано после ответа» открытый диалог тоже считаем:
        // сообщение прочитано не когда на него посмотрели, а когда ответили.
        const openChatId = markReadOnReplyOnly ? undefined : selectedLead?.chat_id
        const bumpedChatIds = Object.entries(chatUnreadBumps)
            .filter(([chatId, count]) => count !== processed[Number(chatId)] && Number(chatId) !== openChatId)
            .map(([chatId]) => Number(chatId))
        processedChatBumpsRef.current = chatUnreadBumps
        if (!bumpedChatIds.length) return
        // Сообщение в диалог, которого в списке нет (новый собеседник, диалог
        // вернулся в «Ответили»), - подтягиваем список, не дожидаясь обновления
        // страницы. Несколько сообщений подряд - один запрос.
        const known = new Set(workItemsRef.current.map((item) => item.chat_id))
        if (bumpedChatIds.some((chatId) => !known.has(chatId))) {
            if (listRefreshTimerRef.current) window.clearTimeout(listRefreshTimerRef.current)
            listRefreshTimerRef.current = window.setTimeout(() => {
                listRefreshTimerRef.current = null
                void refreshWorkItemsSilently()
            }, 700)
        }
        setLeads((items) => items.map((item) =>
            item.chat_id && bumpedChatIds.includes(item.chat_id)
                ? { ...item, unread_count: (item.unread_count || 0) + 1 }
                : item
        ))
        setWorkItems((items) => items.map((item) =>
            bumpedChatIds.includes(item.chat_id)
                ? { ...item, unread_count: (item.unread_count || 0) + 1, last_message_at: new Date().toISOString() }
                : item
        ))
    }, [chatUnreadBumps, markReadOnReplyOnly, selectedLead?.chat_id, refreshWorkItemsSilently])

    // Проверка на дубль закончилась - перечитываем строку списка и шапку.
    const processedDuplicateChecksRef = useRef<Record<number, number>>({})
    const replaceWorkItem = useCallback((fresh: ChatWorkItem) => {
        setWorkItems((items) => items.map((item) => item.chat_id === fresh.chat_id ? { ...item, duplicate: fresh.duplicate } : item))
        setSelectedItem((current) => current && current.chat_id === fresh.chat_id ? { ...current, duplicate: fresh.duplicate } : current)
    }, [])
    useEffect(() => {
        const processed = processedDuplicateChecksRef.current
        const changed = Object.entries(duplicateChecks)
            .filter(([chatId, count]) => count !== processed[Number(chatId)])
            .map(([chatId]) => Number(chatId))
        processedDuplicateChecksRef.current = duplicateChecks
        const shown = new Set(workItemsRef.current.map((item) => item.chat_id))
        if (selectedItem?.chat_id) shown.add(selectedItem.chat_id)
        changed.filter((chatId) => shown.has(chatId)).forEach((chatId) => {
            chatWorkspaceApi.getItem(chatId).then(({ data }) => replaceWorkItem(data)).catch(() => undefined)
        })
    }, [duplicateChecks, selectedItem?.chat_id, replaceWorkItem])

    const recheckDuplicate = useCallback(async () => {
        if (!selectedItem?.chat_id) return
        setRechecking(true)
        try {
            const { data } = await chatWorkspaceApi.recheckDuplicate(selectedItem.chat_id)
            replaceWorkItem(data)
        } catch (error) {
            message.error(extractError(error))
        } finally {
            setRechecking(false)
        }
    }, [selectedItem?.chat_id, replaceWorkItem])

    useEffect(() => {
        setEditingMessage(null)
        setReplyTo(null)
        setSelectedFile(null)
        setText('')
    }, [selectedLead?.id])

    const scrollToBottom = useCallback((behavior: ScrollBehavior = 'smooth') => {
        messagesEndRef.current?.scrollIntoView({ behavior, block: 'end' })
    }, [])

    const handleMessagesScroll = () => {
        const container = messagesContainerRef.current
        if (!container) return
        const distanceToBottom = container.scrollHeight - container.scrollTop - container.clientHeight
        setShowScrollBottom(distanceToBottom > 180)
        if (container.scrollTop < 60 && hasEarlierMessages && !loadingEarlier && !loadingMessages) {
            void loadEarlierMessages()
        }
    }

    useEffect(() => {
        if (skipAutoScrollRef.current) {
            skipAutoScrollRef.current = false
            return
        }
        if (selectedLead) {
            window.setTimeout(() => scrollToBottom('auto'), 0)
        }
    }, [selectedLead?.id, messagesList.length, scrollToBottom])

    useEffect(() => {
        const closeMenu = () => {
            setContextMenu(null)
            setLeadContextMenu(null)
        }
        window.addEventListener('click', closeMenu)
        window.addEventListener('scroll', closeMenu, true)
        return () => {
            window.removeEventListener('click', closeMenu)
            window.removeEventListener('scroll', closeMenu, true)
        }
    }, [])

    // Свежесть статусов CRM спрашиваем редко: она меняется раз в минуту и
    // одинакова для всех диалогов, поэтому это один лёгкий запрос на страницу.
    useEffect(() => {
        let alive = true
        const load = async () => {
            try {
                const { data } = await chatWorkspaceApi.getCrmStatusFreshness()
                if (!alive) return
                setCrmStatusStale(data.enabled ? { stale: data.stale, minutes_ago: data.minutes_ago } : null)
            } catch {
                // Признак свежести - подсказка, а не функция: молча пропускаем.
            }
        }
        load()
        const timer = window.setInterval(load, 120000)
        return () => { alive = false; window.clearInterval(timer) }
    }, [])

    useEffect(() => {
        const token = localStorage.getItem('token')
        if (!token || !selectedLead) return
        const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
        const ws = new WebSocket(`${protocol}//${window.location.host}/ws?token=${token}`)
        const heartbeat = window.setInterval(() => {
            if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: 'ping' }))
        }, 25000)
        ws.onmessage = (event) => {
            try {
                const data = JSON.parse(event.data)
                if (
                    data.type === 'messages_read' &&
                    selectedLead.chat_id &&
                    data.db_chat_id &&
                    Number(data.db_chat_id) === Number(selectedLead.chat_id)
                ) {
                    setMessagesList((items) => items.map((item) => {
                        const tgId = Number(item.tg_message_id || 0)
                        return item.outgoing && tgId > 0 && tgId <= Number(data.max_message_id)
                            ? { ...item, is_read: true, delivery_status: 'read' }
                            : item
                    }))
                }
                if (data.type === 'template_settings') {
                    setTemplatesEnabled(Boolean(data.enabled))
                    if (data.enabled) loadTemplates()
                    else setTemplatesOpen(false)
                }
                if (
                    data.type === 'chat_update' &&
                    data.update_type === 'reactions' &&
                    selectedLead.chat_id &&
                    Number(data.data?.db_chat_id) === Number(selectedLead.chat_id)
                ) {
                    const messageId = Number(data.data?.message_id || 0)
                    // Своё действие уже отрисовано оптимистично, ответ сервера
                    // придёт следом - эхо на него игнорируем.
                    if (messageId && !pendingReactionsRef.current.has(messageId)) {
                        setMessagesList((items) => items.map((row) => (
                            row.id === messageId ? { ...row, reactions: data.data?.reactions ?? null } : row
                        )))
                    }
                }
                if (data.type === 'new_message' && data.db_chat_id && selectedLead.chat_id && Number(data.db_chat_id) === Number(selectedLead.chat_id)) {
                    const wsMessage = data.message
                    const dbMessageId = Number(wsMessage?.db_message_id || 0)
                    if (wsMessage && dbMessageId > 0) {
                        // Сообщение уже сохранено в БД backend'ом - добавляем его в состояние
                        // из WebSocket-payload вместо перезагрузки всей страницы истории.
                        const contentType = wsMessage.content_type || 'text'
                        const appended: LeadMessage = {
                            id: dbMessageId,
                            tg_message_id: Number(wsMessage.id) || null,
                            text: wsMessage.text ?? null,
                            text_html: wsMessage.text_html ?? null,
                            date: wsMessage.date || new Date().toISOString(),
                            outgoing: Boolean(wsMessage.outgoing),
                            operator_id: wsMessage.operator_id ?? null,
                            operator_name: wsMessage.operator_name ?? null,
                            // Подпись «на какой юзернейм писал лид»: снимок на момент
                            // сообщения приходит в уведомлении, номер - на случай,
                            // если юзернейма у аккаунта нет.
                            account_username: wsMessage.account_username ?? selectedLead.account_username ?? null,
                            account_phone: wsMessage.account_phone ?? selectedLead.account_phone ?? null,
                            message_account_username: wsMessage.account_username ?? null,
                            account_id: wsMessage.account_id ?? null,
                            content_type: contentType,
                            reply_to: wsMessage.reply_to_message_id ?? null,
                            is_pinned: false,
                            media_file_name: wsMessage.media_file_name ?? null,
                            media_file_path: contentType !== 'text' && contentType !== 'action' ? `${selectedLead.id}/media/${dbMessageId}` : null,
                            media_mime_type: wsMessage.media_mime_type ?? null,
                            is_read: false,
                            is_edited: false,
                            delivery_status: wsMessage.outgoing ? 'sent' : 'received',
                        }
                        setMessagesList((items) => items.some((item) => Number(item.id) === dbMessageId) ? items : [...items, appended])
                    } else {
                        void loadMessages()
                    }
                    if (data.message && !data.message.outgoing) {
                        chatsApi.markAsRead(selectedLead.chat_id, !markReadOnReplyOnly)
                            .then(() => {
                                if (markReadOnReplyOnly) return
                                const chatId = selectedLead.chat_id
                                const leadId = selectedLead.id
                                setLeads((items) => items.map((item) => item.id === leadId ? { ...item, unread_count: 0 } : item))
                                setWorkItems((items) => items.map((item) => item.chat_id === chatId ? { ...item, unread_count: 0 } : item))
                            })
                            .catch((error: unknown) => console.error('Failed to mark lead chat as read:', error))
                    }
                }
                if (data.type === 'edit_message' && data.db_chat_id && selectedLead.chat_id && Number(data.db_chat_id) === Number(selectedLead.chat_id)) {
                    const editedDbId = Number(data.message?.db_id || 0)
                    const editedTgId = Number(data.message?.id || data.message?.tg_message_id || 0)
                    setMessagesList((items) => items.map((item) => {
                        const itemTgId = Number(item.tg_message_id || 0)
                        const isSameMessage = (editedDbId > 0 && Number(item.id) === editedDbId) || (editedTgId > 0 && itemTgId === editedTgId)
                        // Разметка меняется вместе с текстом: если её нет в событии, сообщение
                        // показываем простым текстом, а не со старым форматированием.
                        return isSameMessage
                            ? { ...item, text: data.message?.text ?? item.text, text_html: data.message?.text_html ?? null, is_edited: true }
                            : item
                    }))
                }
                if (data.type === 'delete_message' && data.db_chat_id && selectedLead.chat_id && Number(data.db_chat_id) === Number(selectedLead.chat_id)) {
                    const deletedId = Number(data.message_id || data.message?.id)
                    setMessagesList((items) => items.filter((item) => Number(item.tg_message_id || item.id) !== deletedId))
                }
            } catch {
                return
            }
        }
        return () => {
            window.clearInterval(heartbeat)
            ws.close()
        }
    }, [selectedLead, loadMessages, markReadOnReplyOnly])

    const refreshCurrentLead = (updatedLead: Lead) => {
        setSelectedLead(updatedLead)
        setLeads((items) => items.map((item) => item.id === updatedLead.id ? updatedLead : item))
    }

    // Выбор аккаунта показывается, только если в переписке их больше одного.
    const senderAccounts = senderInfo?.accounts ?? []
    const multiSender = senderAccounts.length > 1
    const findSender = (id?: number | null) => (id ? senderAccounts.find((account) => account.id === id) : undefined)
    // Цитировать можно только с аккаунта, на который пришло сообщение: так
    // устроен Telegram - у каждого аккаунта своя переписка с лидом.
    const quoteAccountId = multiSender && replyTo?.account_id ? replyTo.account_id : null
    const effectiveSenderId = quoteAccountId ?? senderId ?? senderInfo?.main_account_id ?? null
    const effectiveSender = findSender(effectiveSenderId)
    const lastIncomingSender = findSender(senderInfo?.last_incoming_account_id)
    const senderHintKey = lastIncomingSender
        ? `${selectedLeadId}:${lastIncomingSender.id}:${lastIncomingSender.last_incoming_at || ''}`
        : null
    const showSenderHint = Boolean(
        multiSender && !replyTo && lastIncomingSender
        && lastIncomingSender.id !== effectiveSenderId
        && senderHintDismissed !== senderHintKey,
    )

    const sendMessage = async (forceNewAccount = false) => {
        if (!selectedLead || (!composerPlain && !selectedFile)) return
        // Разметку отправляем, только если форматирование есть: иначе уходит
        // обычный текст, как раньше.
        const formatted = hasTgFormatting(text) ? text : null
        setSending(true)
        try {
            if (editingMessage) {
                const nextText = composerPlain
                await leadsApi.editMessage(selectedLead.id, editingMessage.id, nextText, formatted)
                setMessagesList((items) => items.map((item) => (
                    item.id === editingMessage.id ? { ...item, text: nextText, text_html: formatted, is_edited: true } : item
                )))
                setText('')
                setEditingMessage(null)
                message.success('Сообщение отредактировано')
                return
            }
            // Основной аккаунт чужого профиля - ответ уходит с разрешённого,
            // даже если в списке он один.
            const fromAccountId = (multiSender || senderInfo?.main_blocked) ? effectiveSenderId : null
            const response = selectedFile
                ? await leadsApi.sendMedia(selectedLead.id, selectedFile, composerPlain, forceNewAccount, replyTo?.tg_message_id || null, formatted, fromAccountId)
                : await leadsApi.sendMessage(selectedLead.id, composerPlain, forceNewAccount, replyTo?.tg_message_id || null, formatted, fromAccountId)
            setSenderError(null)
            refreshCurrentLead(response.data)
            // Ответили - значит непрочитанных в этом диалоге больше нет.
            if (activeChatId) {
                chatsApi.markAsRead(activeChatId).catch(() => undefined)
                setWorkItems((items) => items.map((item) =>
                    item.chat_id === activeChatId ? { ...item, unread_count: 0 } : item))
                setSelectedItem((current) =>
                    current && current.chat_id === activeChatId ? { ...current, unread_count: 0 } : current)
                setLeads((items) => items.map((item) =>
                    item.chat_id === activeChatId ? { ...item, unread_count: 0 } : item))
            }
            // Пул мог подменить недоступный аккаунт: оператор должен узнать об
            // этом сразу - для лида это смена собеседника.
            if (response.data.account_switch_notice) {
                message.warning(response.data.account_switch_notice, 8)
                await loadWorkItems()
            }
            setText('')
            setReplyTo(null)
            setSelectedFile(null)
            if (fileInputRef.current) fileInputRef.current.value = ''
            await loadMessages()
            window.setTimeout(() => scrollToBottom(), 0)
        } catch (error) {
            // Не ушло с выбранного аккаунта - причина остаётся над полем ввода,
            // текст не пропадает, сотрудник сам выбирает другой аккаунт.
            if (multiSender && effectiveSenderId && effectiveSenderId !== senderInfo?.main_account_id) {
                setSenderError(extractError(error))
            } else {
                message.error(extractError(error))
            }
            await loadLeads()
        } finally {
            setSending(false)
        }
    }

    const selectAttachment = (file?: File) => {
        if (!file || editingMessage) return
        setSelectedFile(file)
    }

    // Ctrl+V со скриншотом в буфере прикрепляет его, как в Telegram, - дальше
    // всё как со скрепкой: можно добавить подпись и отправить. Если в буфере
    // есть и текст, вставляем текст: таблицы и документы кладут рядом со словами
    // ещё и картинку, и без этой проверки вместо текста улетал бы скриншот.
    const handleComposerPaste = (event: ClipboardEvent<HTMLDivElement>) => {
        if (editingMessage) return
        const data = event.clipboardData
        const file = data?.files?.[0]
        if (!file || (data.getData('text/plain') || '').trim()) return
        event.preventDefault()
        event.stopPropagation()
        // У скриншота из буфера безликое имя «image.png» - даём понятное.
        const named = !file.name || /^image\.\w+$/i.test(file.name)
            ? new File([file], `screenshot-${dayjs().format('YYYY-MM-DD-HH-mm-ss')}.${(file.type.split('/')[1] || 'png').replace('jpeg', 'jpg')}`, { type: file.type })
            : file
        selectAttachment(named)
    }

    const continueWithAnotherAccount = async () => {
        if (!selectedLead) return
        setSending(true)
        try {
            const response = await leadsApi.continueWithAnotherAccount(selectedLead.id)
            refreshCurrentLead(response.data)
            message.success('Назначен другой рабочий TG-аккаунт')
        } catch (error) {
            message.error(extractError(error))
        } finally {
            setSending(false)
        }
    }

    const deleteSelectedLead = async () => {
        if (!selectedLead) return
        const leadId = selectedLead.id
        try {
            await leadsApi.delete(leadId)
            message.success('Лид удален')
            setLeads((items) => items.filter((lead) => lead.id !== leadId))
            setSelectedLead(null)
            setMessagesList([])
        } catch (error) {
            message.error(extractError(error))
        }
    }

    const blockSelectedLead = async () => {
        if (!selectedLead) return
        setSending(true)
        try {
            const response = await leadsApi.block(selectedLead.id)
            refreshCurrentLead(response.data)
            message.success('Лид заблокирован в Telegram')
        } catch (error) {
            message.error(extractError(error))
        } finally {
            setSending(false)
        }
    }

    const unblockSelectedLead = async () => {
        if (!selectedLead) return
        setSending(true)
        try {
            const response = await leadsApi.unblock(selectedLead.id)
            refreshCurrentLead(response.data)
            message.success('Блокировка снята')
        } catch (error) {
            message.error(extractError(error))
        } finally {
            setSending(false)
        }
    }

    const copyMessageText = async (item: LeadMessage) => {
        const value = item.text || item.media_file_name || ''
        if (navigator.clipboard?.writeText) {
            await navigator.clipboard.writeText(value)
        } else {
            const area = document.createElement('textarea')
            area.value = value
            area.style.position = 'fixed'
            area.style.left = '-9999px'
            document.body.appendChild(area)
            area.select()
            document.execCommand('copy')
            area.remove()
        }
        message.success('Скопировано')
        setContextMenu(null)
    }

    const startEditMessage = (item: LeadMessage) => {
        if (!item.outgoing || !item.text) return
        setEditingMessage(item)
        setReplyTo(null)
        setSelectedFile(null)
        setText(item.text_html || plainToTg(item.text))
        setContextMenu(null)
        window.setTimeout(() => composerRef.current?.focus(), 0)
    }

    // Поиск из шапки: список фильтруется на месте, а совпадения в тексте
    // переписки ищет сервер - вся история в браузере не лежит.
    const [messageHits, setMessageHits] = useState<MessageSearchHit[]>([])
    const [searchingMessages, setSearchingMessages] = useState(false)
    const [searchError, setSearchError] = useState('')

    useEffect(() => {
        const query = searchQuery.trim()
        if (query.length < SEARCH_MIN_LENGTH) {
            setMessageHits([])
            setSearchingMessages(false)
            setSearchError('')
            return
        }
        setSearchingMessages(true)
        setSearchError('')

        // Пауза, чтобы не дёргать сервер на каждую букву, и отмена уже
        // улетевшего запроса: иначе медленный ранний ответ перезапишет
        // свежий и оператор увидит выдачу не по тому, что набрал.
        const controller = new AbortController()
        const timer = window.setTimeout(() => {
            leadsApi.searchMessages(query, SEARCH_HITS_LIMIT, controller.signal)
                .then((response) => setMessageHits(response.data))
                .catch((error) => {
                    if (controller.signal.aborted) return
                    setMessageHits([])
                    setSearchError(extractError(error))
                })
                .finally(() => {
                    if (!controller.signal.aborted) setSearchingMessages(false)
                })
        }, 350)

        return () => {
            window.clearTimeout(timer)
            controller.abort()
        }
    }, [searchQuery])

    const openSearchHit = (hit: MessageSearchHit) => {
        const lead = leads.find((item) => item.id === hit.lead_id)
        if (lead) {
            setSelectedLead(lead)
            setSelectedItem(workItems.find((item) => item.lead_id === hit.lead_id) || null)
        } else {
            navigate(`/chats?leadId=${hit.lead_id}`)
        }
        // История подгружается асинхронно, поэтому подсветку откладываем.
        window.setTimeout(() => jumpToMessage(hit.tg_message_id || hit.message_id), 600)
    }

    useEffect(() => {
        if (!selectedLead) return
        let cancelled = false
        leadsApi.getAvailableReactions(selectedLead.id)
            .then((response) => { if (!cancelled) setAvailableReactions(response.data) })
            .catch(() => undefined)
        return () => { cancelled = true }
    }, [selectedLead?.id])

    // Быстрый ряд и пикер строятся только из того, что Telegram реально примет.
    const allowedReactions = useMemo(
        () => new Map(availableReactions.map((item) => [normalizeReaction(item.emoji), item.emoji])),
        [availableReactions],
    )

    const visibleQuickReactions = useMemo(() => {
        if (!allowedReactions.size) return quickReactions
        const picked: string[] = []
        for (const emoji of quickReactions) {
            const exact = allowedReactions.get(normalizeReaction(emoji))
            if (exact && !picked.includes(exact)) picked.push(exact)
        }
        // Если сохранённый ряд поредел после фильтрации, добираем из начала
        // списка Telegram - там идут самые ходовые реакции.
        for (const item of availableReactions) {
            if (picked.length >= QUICK_REACTIONS_LIMIT) break
            if (!picked.includes(item.emoji)) picked.push(item.emoji)
        }
        return picked.slice(0, QUICK_REACTIONS_LIMIT)
    }, [quickReactions, allowedReactions, availableReactions])

    const filteredAvailableReactions = useMemo(() => {
        const query = reactionQuery.trim().toLowerCase()
        if (!query) return availableReactions
        // Ищем по русским синонимам, английскому названию от Telegram и по
        // самому эмодзи - оператор может вставить его из другого чата.
        return availableReactions.filter((item) => {
            const keywords = REACTION_KEYWORDS[normalizeReaction(item.emoji)] || ''
            return (
                keywords.includes(query)
                || (item.title || '').toLowerCase().includes(query)
                || normalizeReaction(item.emoji) === normalizeReaction(query)
            )
        })
    }, [availableReactions, reactionQuery])

    useEffect(() => {
        chatWorkspaceApi.getOutcomes()
            .then((response) => setCloseReasonGroups(response.data.groups || []))
            .catch(() => undefined)
        chatWorkspaceApi.getReopenOutcomes()
            .then((response) => setReopenReasonGroups(response.data.groups || []))
            .catch(() => undefined)
    }, [])

    const loadTemplates = useCallback(async () => {
        try {
            const response = await messageTemplatesApi.getTemplates()
            setTemplates(response.data)
        } catch {
            setTemplates([])
        }
    }, [])

    useEffect(() => {
        let cancelled = false
        messageTemplatesApi.getSettings()
            .then((response) => {
                if (cancelled) return
                setTemplatesEnabled(response.data.enabled)
                if (response.data.enabled) loadTemplates()
            })
            .catch(() => undefined)
        return () => { cancelled = true }
    }, [loadTemplates])

    const filteredTemplates = useMemo(() => {
        const query = templateQuery.trim().toLowerCase()
        if (!query) return templates
        return templates.filter((template) => (
            `${template.name} ${template.category || ''} ${template.is_formatted ? tgToPlain(template.content) : template.content}`.toLowerCase().includes(query)
        ))
    }, [templates, templateQuery])

    // Группировка по категориям - как в списке на образце.
    const templateGroups = useMemo(() => {
        const groups = new Map<string, MessageTemplate[]>()
        for (const template of filteredTemplates) {
            const key = template.category || 'Без категории'
            if (!groups.has(key)) groups.set(key, [])
            groups.get(key)!.push(template)
        }
        return Array.from(groups.entries())
    }, [filteredTemplates])

    const focusComposer = () => {
        window.setTimeout(() => composerRef.current?.focus(), 0)
    }

    const openTemplates = () => {
        setTemplateQuery('')
        setTemplateIndex(0)
        setTemplatesOpen(true)
        window.setTimeout(() => {
            document.querySelector<HTMLInputElement>('.lead-template-search input')?.focus()
        }, 0)
    }

    const closeTemplates = (returnFocus = true) => {
        setTemplatesOpen(false)
        setTemplateQuery('')
        if (returnFocus) focusComposer()
    }

    const insertTemplate = (template: MessageTemplate) => {
        // Шаблон вставляется в позицию курсора вместе со своим форматированием.
        setTemplatesOpen(false)
        setTemplateQuery('')
        window.setTimeout(() => composerRef.current?.insertTg(templateToTg(template.content, template.is_formatted)), 0)

        // Счётчик применений поднимает частые шаблоны наверх списка.
        messageTemplatesApi.markUsed(template.id)
            .then(() => setTemplates((items) => items.map((item) => (
                item.id === template.id ? { ...item, usage_count: item.usage_count + 1 } : item
            ))))
            .catch(() => undefined)
    }

    const handleTemplateKeyDown = (event: React.KeyboardEvent) => {
        if (event.key === 'Escape') {
            event.preventDefault()
            closeTemplates()
            return
        }
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault()
            if (!filteredTemplates.length) return
            const shift = event.key === 'ArrowDown' ? 1 : -1
            setTemplateIndex((current) => (current + shift + filteredTemplates.length) % filteredTemplates.length)
            return
        }
        if (event.key === 'Enter') {
            event.preventDefault()
            const target = filteredTemplates[templateIndex] || filteredTemplates[0]
            if (target) insertTemplate(target)
        }
    }

    const rememberQuickReaction = (emoji: string) => {
        setQuickReactions((current) => {
            const next = [emoji, ...current.filter((item) => item !== emoji)].slice(0, QUICK_REACTIONS_LIMIT)
            localStorage.setItem(QUICK_REACTIONS_KEY, JSON.stringify(next))
            return next
        })
    }

    const toggleReaction = async (item: LeadMessage, emoji: string) => {
        if (!selectedLead) return
        // Повторный клик по своей реакции снимает её.
        const own = (item.reactions || []).find((reaction) => reaction.chosen)
        const nextEmoji = own?.emoji === emoji ? '' : emoji
        const previous = item.reactions ?? null

        setContextMenu(null)
        setReactionPickerFor(null)
        if (nextEmoji) rememberQuickReaction(nextEmoji)

        const optimistic = applyOwnReaction(previous, nextEmoji)
        setMessagesList((items) => items.map((row) => (row.id === item.id ? { ...row, reactions: optimistic } : row)))
        pendingReactionsRef.current.add(item.id)

        try {
            const response = await leadsApi.reactToMessage(selectedLead.id, item.id, nextEmoji)
            const confirmed = response.data?.reactions ?? null
            // Аватар аккаунта мог подтянуться только что - раздаём его всем
            // сообщениям того же аккаунта, чтобы чипы подписались сразу.
            const avatar = response.data?.account_avatar_url || null
            setMessagesList((items) => items.map((row) => {
                if (row.id === item.id) {
                    return { ...row, reactions: confirmed, account_avatar_url: avatar || row.account_avatar_url }
                }
                if (avatar && !row.account_avatar_url && row.account_id === item.account_id) {
                    return { ...row, account_avatar_url: avatar }
                }
                return row
            }))
        } catch (error) {
            setMessagesList((items) => items.map((row) => (row.id === item.id ? { ...row, reactions: previous } : row)))
            message.error(extractError(error))
        } finally {
            pendingReactionsRef.current.delete(item.id)
        }
    }

    const pinMessage = async (item: LeadMessage) => {
        if (!selectedLead) return
        try {
            await leadsApi.pinMessage(selectedLead.id, item.id)
            setMessagesList((items) => items.map((messageItem) => messageItem.id === item.id ? { ...messageItem, is_pinned: true } : messageItem))
            setPinnedJumpIndex(0)
            message.success('Сообщение закреплено')
        } catch (error) {
            message.error(extractError(error))
        } finally {
            setContextMenu(null)
        }
    }

    const unpinMessage = async (item: LeadMessage) => {
        if (!selectedLead) return
        try {
            await leadsApi.unpinMessage(selectedLead.id, item.id)
            setMessagesList((items) => items.map((messageItem) => messageItem.id === item.id ? { ...messageItem, is_pinned: false } : messageItem))
            setPinnedJumpIndex(0)
            message.success('Сообщение откреплено')
        } catch (error) {
            message.error(extractError(error))
        } finally {
            setContextMenu(null)
        }
    }

    const deleteMessage = async (item: LeadMessage, revoke: boolean) => {
        if (!selectedLead) return
        try {
            const response = await leadsApi.deleteMessage(selectedLead.id, item.id, revoke)
            setMessagesList((items) => items.filter((messageItem) => messageItem.id !== item.id))
            // Заблокированный аккаунт удалить у собеседника ничего не может -
            // сервер об этом предупреждает, и оператор должен это видеть.
            const warning = (response?.data as { warning?: string } | undefined)?.warning
            if (warning) {
                message.warning(warning, 6)
            } else {
                message.success(revoke ? 'Удалено у всех' : 'Удалено у себя')
            }
        } catch (error) {
            message.error(extractError(error))
        } finally {
            setContextMenu(null)
        }
    }

    const jumpToPinnedMessage = () => {
        if (!pinnedJumpMessages.length) return
        const item = pinnedJumpMessages[pinnedJumpIndex % pinnedJumpMessages.length]
        setPinnedJumpIndex((current) => current + 1)
        const node = document.getElementById(`lead-message-${item.id}`)
        if (node) {
            node.scrollIntoView({ behavior: 'smooth', block: 'center' })
            setHighlightedMessageId(item.id)
            window.setTimeout(() => setHighlightedMessageId((current) => current === item.id ? null : current), 1400)
        }
    }

    const jumpToMessage = (messageId?: number | null) => {
        if (!messageId) return
        const target = messagesList.find((item) => {
            const tgId = Number(item.tg_message_id || 0)
            return Number(item.id) === Number(messageId) || (tgId > 0 && tgId === Number(messageId))
        })
        if (!target) {
            message.info('Исходное сообщение не загружено в текущей истории')
            return
        }
        const node = document.getElementById(`lead-message-${target.id}`)
        if (node) {
            node.scrollIntoView({ behavior: 'smooth', block: 'center' })
            setHighlightedMessageId(target.id)
            window.setTimeout(() => setHighlightedMessageId((current) => current === target.id ? null : current), 1400)
        }
    }

    return (
        <>
        <div className="lead-workspace lead-chats-page" style={{
            display: 'grid',
            gridTemplateColumns: cardDrawerLeadId !== null
                ? 'minmax(0, 1fr)'
                : cardPanelOpen && selectedLead ? '300px minmax(0, 1fr) 300px' : '336px minmax(0, 1fr)',
            // Карточка выезжает поверх страницы, поэтому переписку ужимаем сами -
            // иначе её шапка и поле ввода оказываются под карточкой.
            paddingRight: cardDrawerLeadId !== null ? CARD_DRAWER_WIDTH : undefined,
            height: 'calc(100vh - 64px)',
            gap: 16,
        }}>

            {leadContextMenu && (
                <div
                    className="lead-message-context-menu"
                    style={{ left: leadContextMenu.x, top: leadContextMenu.y }}
                    onClick={(event) => event.stopPropagation()}
                >
                    <button type="button" onClick={() => markChatReadForced(leadContextMenu.chatId, leadContextMenu.lead?.id ?? null)}>
                        <span>✓</span>Отметить прочитанным
                    </button>

                    <button type="button" onClick={() => removeChatFromList(leadContextMenu.chatId)}>
                        <span>🗑</span>Удалить чат
                    </button>

                    {foldersEnabled && leadContextMenu.lead && (
                        <>
                            <div className="lead-context-menu-divider" />
                            <div className="lead-context-menu-title">Переместить в папку</div>
                            {folders.length === 0 && <div className="lead-context-menu-empty">Папок пока нет</div>}
                            {folders.map((folder) => (
                                <button key={folder.id} type="button" onClick={() => assignLeadToFolder(leadContextMenu.lead!, folder.id)}>
                                    <span style={{ color: folder.color }}>●</span>{folder.name}
                                </button>
                            ))}
                            {folderAssignments[String(leadContextMenu.lead.id)] && (
                                <button type="button" onClick={() => assignLeadToFolder(leadContextMenu.lead!, null)}>
                                    <span>✕</span>Убрать из папки
                                </button>
                            )}
                            <button type="button" onClick={() => { setLeadContextMenu(null); setFoldersModalOpen(true) }}>
                                <span>⚙</span>Настройка папок
                            </button>
                        </>
                    )}
                </div>
            )}

            <Modal
                title="Добавить лида"
                open={draftOpen}
                onCancel={() => setDraftOpen(false)}
                okText={draft?.scenario === 'create' ? 'Создать'
                    : draft?.scenario === 'unavailable' ? 'Проверить ещё раз'
                        : 'Объединить с карточкой'}
                okButtonProps={{ danger: draft?.scenario === 'merge_fraud' }}
                cancelText="Отмена"
                confirmLoading={draftSaving}
                onOk={draft?.scenario === 'create' ? createLeadFromChat
                    : draft?.scenario === 'unavailable' ? reloadDraft
                        : mergeLeadFromChat}
                width={620}
            >
                <LeadDraftDuplicate draft={draft} />
                {draft?.scenario === 'create' && (
                    <Form form={draftForm} layout="vertical">
                        <Form.Item
                            name="title"
                            label="Название лида"
                            rules={[{ required: true, message: 'У лида нет TG username — укажите название' }]}
                            extra={draft?.title_locked ? 'Название — TG username лида' : 'У лида нет TG username — введите название вручную'}
                        >
                            <Input disabled={Boolean(draft?.title_locked)} />
                        </Form.Item>
                        {draft?.telegram_nickname && (
                            <Form.Item name="telegram_nickname" label="TG Nickname">
                                <Input disabled />
                            </Form.Item>
                        )}
                        <Form.Item
                            name="source"
                            label="Источник"
                            rules={[{ required: true, message: 'Выберите источник' }]}
                            extra="Если лид не сказал, откуда пришёл, оставьте «Написал в ЛС в TG»"
                        >
                            <Select
                                showSearch
                                placeholder="Выберите источник"
                                options={draftSources.map((item) => ({ value: item, label: item }))}
                            />
                        </Form.Item>
                        <Form.Item name="geo" label="ГЕО" rules={[{ required: true, message: 'Выберите гео' }]}>
                            <Select
                                showSearch
                                placeholder="Выберите гео"
                                options={geos.map((geo) => ({ value: geo.name, label: geo.name }))}
                            />
                        </Form.Item>
                        <Form.Item name="temperature" label="Температура" rules={[{ required: true, message: 'Выберите температуру' }]}>
                            <Select options={TEMPERATURE_OPTIONS} />
                        </Form.Item>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            Карточка сначала создаётся в CRM Wiseway, ответственный — вы.
                        </Text>
                    </Form>
                )}
            </Modal>

            <Modal
                title="Передать лида"
                open={transferOpen}
                onCancel={() => setTransferOpen(false)}
                okText="Передать"
                cancelText="Отмена"
                confirmLoading={actionLoading}
                onOk={() => {
                    if (!activeChatId || !transferTo) {
                        message.warning('Выберите сотрудника')
                        return
                    }
                    setTransferOpen(false)
                    // Диалог уходит другому - в своём списке его больше не будет,
                    // поэтому оставлять его открытым незачем.
                    runChatAction(() => chatWorkspaceApi.transfer(activeChatId, transferTo), 'Лид передан')
                }}
            >
                <Text type="secondary">
                    Диалог, карточка лида и ответственный перейдут выбранному сотруднику. Ему придёт уведомление.
                </Text>
                <Select
                    showSearch
                    optionFilterProp="label"
                    style={{ width: '100%', marginTop: 12 }}
                    placeholder="Кому передать"
                    value={transferTo}
                    onChange={setTransferTo}
                    options={staff
                        .filter((item) => item.id !== selectedItem?.assigned_operator_id)
                        .map((item) => ({ value: item.id, label: item.full_name || item.username }))}
                />
            </Modal>

            <Modal
                title="Отложить диалог"
                open={postponeOpen}
                onCancel={() => setPostponeOpen(false)}
                okText="Отложить"
                cancelText="Отмена"
                confirmLoading={actionLoading}
                onOk={() => {
                    if (!activeChatId || !postponeComment.trim()) return
                    setPostponeOpen(false)
                    runChatAction(() => chatWorkspaceApi.postpone(activeChatId, postponeComment.trim()), 'Диалог отложен')
                }}
            >
                <Text type="secondary">Диалог останется закреплён за вами. Если человек напишет сам, он вернётся в «Активные».</Text>
                <Input.TextArea
                    rows={4}
                    style={{ marginTop: 10 }}
                    placeholder="Причина: например, просил написать через две недели"
                    value={postponeComment}
                    onChange={(event) => setPostponeComment(event.target.value)}
                />
            </Modal>

            <Modal
                title="Переоткрыть диалог"
                open={reopenOpen}
                onCancel={() => setReopenOpen(false)}
                okText="Переоткрыть"
                cancelText="Отмена"
                confirmLoading={actionLoading}
                okButtonProps={{ disabled: !reopenOutcome }}
                onOk={() => {
                    if (!activeChatId || !reopenOutcome) return
                    setReopenOpen(false)
                    runChatAction(
                        () => chatWorkspaceApi.reopen(activeChatId, reopenOutcome, reopenComment.trim() || undefined),
                        'Диалог возвращён в работу',
                        { keepOpen: true, nextState: 'active', goToTab: 'active' },
                    )
                }}
            >
                <Space direction="vertical" size={10} style={{ width: '100%' }}>
                    <div>
                        <Text>Вернуть лида в статус</Text>
                        <Select
                            style={{ width: '100%', marginTop: 6 }}
                            value={reopenOutcome}
                            onChange={setReopenOutcome}
                            placeholder="Выберите статус"
                            options={reopenReasonGroups.flatMap((group) => group.reasons.map((reason) => ({
                                value: reason.code,
                                label: reason.label,
                            })))}
                        />
                        {reopenOutcome && (
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                Статус для CRM Wiseway: <code>{reopenOutcome}</code>
                            </Text>
                        )}
                    </div>
                    <Input.TextArea
                        rows={4}
                        placeholder="Комментарий (необязательно)"
                        value={reopenComment}
                        onChange={(event) => setReopenComment(event.target.value)}
                    />
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        Комментарий добавится отдельной записью в ленту карточки Wiseway.
                    </Text>
                </Space>
            </Modal>

            <Modal
                title="Закрыть диалог"
                open={closeOpen}
                onCancel={() => setCloseOpen(false)}
                okText="Закрыть диалог"
                cancelText="Отмена"
                confirmLoading={actionLoading}
                okButtonProps={{ disabled: !closeOutcome }}
                onOk={() => {
                    if (!activeChatId || !closeOutcome) return
                    setCloseOpen(false)
                    runChatAction(
                        () => chatWorkspaceApi.close(activeChatId, closeOutcome, closeComment.trim() || undefined),
                        'Диалог закрыт',
                    )
                }}
            >
                <Space direction="vertical" size={10} style={{ width: '100%' }}>
                    <div>
                        <Text>Причина закрытия</Text>
                        <Select
                            style={{ width: '100%', marginTop: 6 }}
                            value={closeOutcome}
                            onChange={setCloseOutcome}
                            placeholder="Выберите причину"
                            showSearch
                            optionFilterProp="label"
                            // Плоский список: группировка с сервера приходит и задаёт
                            // порядок, но заголовки категорий не показываем.
                            options={closeReasonGroups.flatMap((group) => group.reasons.map((reason) => ({
                                value: reason.code,
                                label: reason.label,
                            })))}
                        />
                        {closeOutcome && (
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                Статус для CRM Wiseway: <code>{closeOutcome}</code>
                            </Text>
                        )}
                    </div>
                    <Input.TextArea
                        rows={4}
                        placeholder="Комментарий (необязательно)"
                        value={closeComment}
                        onChange={(event) => setCloseComment(event.target.value)}
                    />
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        Комментарий добавится отдельной записью в ленту карточки Wiseway. В поле
                        «Причина отказа» попадёт название выбранной причины.
                    </Text>
                </Space>
            </Modal>

            <Modal
                title="Изменить статус"
                open={crmStatusOpen}
                onCancel={() => setCrmStatusOpen(false)}
                okText="Изменить статус лида"
                cancelText="Отмена"
                confirmLoading={crmStatusSaving}
                okButtonProps={{ disabled: !crmStatusValue || crmStatusLoading || !crmStatusHasCard }}
                onOk={saveCrmStatus}
            >
                <Spin spinning={crmStatusLoading}>
                    <Space direction="vertical" size={10} style={{ width: '100%' }}>
                        {!crmStatusHasCard ? (
                            <Alert
                                type="warning"
                                showIcon
                                message="У лида нет ID карточки CRM Wiseway - передавать статус некуда"
                            />
                        ) : (
                            <>
                                <Text type="secondary">
                                    Список статусов - это полный справочник CRM Wiseway. Выбор здесь меняет
                                    только их статус: работу с диалогом в Оркестраторе (взят/отложен/закрыт)
                                    эта кнопка не трогает.
                                </Text>
                                {crmStatusCurrentLabel && (
                                    <Text type="secondary" style={{ fontSize: 12 }}>
                                        Сейчас в CRM Wiseway: <b>{crmStatusCurrentLabel}</b>
                                    </Text>
                                )}
                                <Select
                                    style={{ width: '100%' }}
                                    value={crmStatusValue}
                                    onChange={setCrmStatusValue}
                                    placeholder="Выберите статус"
                                    showSearch
                                    optionFilterProp="label"
                                    options={crmStatusGroups.map((group) => ({
                                        label: group.group,
                                        options: group.reasons.map((reason) => ({
                                            value: reason.code,
                                            label: reason.label,
                                        })),
                                    }))}
                                />
                                {crmStatusValue && (
                                    <Text type="secondary" style={{ fontSize: 12 }}>
                                        Код для CRM Wiseway: <code>{crmStatusValue}</code>
                                    </Text>
                                )}
                            </>
                        )}
                    </Space>
                </Spin>
            </Modal>

            <Modal
                title="Папки чатов"
                open={foldersModalOpen}
                onCancel={() => { setFoldersModalOpen(false); setEditingFolderId(null) }}
                footer={[<Button key="close" onClick={() => { setFoldersModalOpen(false); setEditingFolderId(null) }}>Закрыть</Button>]}
                width={560}
            >
                <Space direction="vertical" size={14} style={{ width: '100%' }}>
                    <Space align="center" size={12}>
                        <Switch checked={foldersEnabled} onChange={toggleFolders} />
                        <Text>Использовать папки</Text>
                    </Space>
                    <Text type="secondary">
                        Папки видны только вам и только в разделе «Чаты». После включения нажмите правой кнопкой
                        мыши на чат, чтобы переместить его в папку.
                    </Text>

                    {foldersEnabled && (
                        <>
                            <div>
                                <Text strong>Новая папка</Text>
                                <Space.Compact style={{ width: '100%', marginTop: 8 }}>
                                    <Input
                                        placeholder="Название папки"
                                        value={newFolderName}
                                        maxLength={100}
                                        onChange={(event) => setNewFolderName(event.target.value)}
                                        onPressEnter={createFolder}
                                    />
                                    <Button type="primary" loading={folderSaving} onClick={createFolder}>Создать</Button>
                                </Space.Compact>
                                <Space size={6} style={{ marginTop: 8 }} wrap>
                                    {FOLDER_COLOR_PRESETS.map((color) => (
                                        <button
                                            key={color}
                                            type="button"
                                            aria-label={`Цвет ${color}`}
                                            onClick={() => setNewFolderColor(color)}
                                            className={newFolderColor === color ? 'chat-folder-color is-active' : 'chat-folder-color'}
                                            style={{ background: color }}
                                        />
                                    ))}
                                </Space>
                            </div>

                            <List
                                header={<Text strong>Мои папки</Text>}
                                dataSource={folders}
                                locale={{ emptyText: <Empty description="Папок пока нет" /> }}
                                renderItem={(folder) => (
                                    <List.Item
                                        actions={editingFolderId === folder.id ? [
                                            <Button key="save" type="link" loading={folderSaving} onClick={saveFolderEdit}>Сохранить</Button>,
                                            <Button key="cancel" type="link" onClick={() => setEditingFolderId(null)}>Отмена</Button>,
                                        ] : [
                                            <Button
                                                key="edit"
                                                type="link"
                                                onClick={() => {
                                                    setEditingFolderId(folder.id)
                                                    setEditingFolderName(folder.name)
                                                    setEditingFolderColor(folder.color)
                                                }}
                                            >
                                                Изменить
                                            </Button>,
                                            <Popconfirm
                                                key="delete"
                                                title="Удалить папку?"
                                                description="Чаты останутся на месте, исчезнет только группировка."
                                                okText="Удалить"
                                                cancelText="Отмена"
                                                onConfirm={() => removeFolder(folder)}
                                            >
                                                <Button type="link" danger>Удалить</Button>
                                            </Popconfirm>,
                                        ]}
                                    >
                                        {editingFolderId === folder.id ? (
                                            <Space direction="vertical" size={6} style={{ width: '100%' }}>
                                                <Input
                                                    value={editingFolderName}
                                                    maxLength={100}
                                                    onChange={(event) => setEditingFolderName(event.target.value)}
                                                />
                                                <Space size={6} wrap>
                                                    {FOLDER_COLOR_PRESETS.map((color) => (
                                                        <button
                                                            key={color}
                                                            type="button"
                                                            aria-label={`Цвет ${color}`}
                                                            onClick={() => setEditingFolderColor(color)}
                                                            className={editingFolderColor === color ? 'chat-folder-color is-active' : 'chat-folder-color'}
                                                            style={{ background: color }}
                                                        />
                                                    ))}
                                                </Space>
                                            </Space>
                                        ) : (
                                            <Space size={8}>
                                                <Tag color={folder.color}>{folder.name}</Tag>
                                                <Text type="secondary">{folder.leads_count} чатов</Text>
                                            </Space>
                                        )}
                                    </List.Item>
                                )}
                            />
                        </>
                    )}
                </Space>
            </Modal>

            {headerSlot && createPortal(
                <Tooltip title="Обновить">
                    <Button
                        type="text"
                        icon={<ReloadOutlined />}
                        onClick={loadLeads}
                        loading={loadingLeads}
                        aria-label="Обновить"
                        style={{
                            width: 32, height: 32, minWidth: 32, borderRadius: 7, marginRight: 8,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            background: 'var(--bg-base)',
                        }}
                    />
                </Tooltip>,
                headerSlot,
            )}

            <aside
                className="lead-chat-sidebar"
                style={{
                    borderRight: '1px solid var(--lead-border)',
                    padding: '0 22px 0 18px',
                    minWidth: 0,
                    // Карточка открыта - список чатов убираем, он только съедает ширину.
                    display: cardDrawerLeadId !== null ? 'none' : undefined,
                }}
            >
                <Space direction="vertical" size={12} style={{ width: '100%', height: '100%' }}>
                    <Select
                        value={selectedGeo}
                        loading={loadingGeos}
                        placeholder="Выберите гео"
                        style={{ width: '100%' }}
                        onChange={(value) => {
                            setSelectedGeo(value)
                            setSelectedLead(null)
                            setMessagesList([])
                        }}
                        options={[
                            { value: ALL_GEOS_VALUE, label: `Все гео (${allGeoAssignedCount})` },
                            ...geos.map((geo) => ({
                                value: geo.name,
                                label: `${geo.name} (${geo.assigned_count})`,
                            })),
                        ]}
                    />

                    {/* Кнопки «Взять в работу» / «Отложить» / «Закрыть» живут в шапке диалога,
                        в боковой панели они дублировались. */}

                    <Select
                        value={workTab}
                        style={{ width: '100%' }}
                        onChange={(value: WorkTab) => { setWorkTab(value); setSelectedItem(null); setSelectedLead(null) }}
                        options={WORK_TABS.map((tab) => ({ value: tab.key, label: `${tab.label} (${workCounts[tab.key] ?? 0})` }))}
                    />

                    {foldersEnabled && (
                        <div className="chat-folder-panel">
                            <div className="chat-folder-panel-head">
                                <span>ПАПКИ</span>
                                <button type="button" onClick={() => setFoldersModalOpen(true)}>Изменить папки</button>
                            </div>
                            <div className="chat-folder-panel-chips">
                                <button
                                    type="button"
                                    className={activeFolderId === ALL_FOLDERS_VALUE ? 'chat-folder-chip is-active' : 'chat-folder-chip'}
                                    onClick={() => setActiveFolderId(ALL_FOLDERS_VALUE)}
                                >
                                    Все {workItems.length}
                                </button>
                                {folders.map((folder) => (
                                    <button
                                        key={folder.id}
                                        type="button"
                                        className={activeFolderId === folder.id ? 'chat-folder-chip is-active' : 'chat-folder-chip'}
                                        style={{
                                            borderColor: folder.color,
                                            color: activeFolderId === folder.id ? '#fff' : folder.color,
                                            background: activeFolderId === folder.id ? folder.color : undefined,
                                        }}
                                        onClick={() => setActiveFolderId(folder.id)}
                                    >
                                        {folder.name} {folder.leads_count > 0 ? folder.leads_count : ''}
                                        {user?.folder_unread_badges && (folderUnread[folder.id] || 0) > 0 && (
                                            <span className="chat-folder-unread">{folderUnread[folder.id]}</span>
                                        )}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                    {!foldersEnabled && (
                        <Button size="small" type="text" icon={<SettingOutlined />} onClick={() => setFoldersModalOpen(true)}>
                            Папки чатов
                        </Button>
                    )}

                    <div className="lead-sort-row">
                        <Select
                            value={leadSort}
                            onChange={setLeadSort}
                            options={[
                                { value: 'activity', label: 'По активности' },
                                { value: 'unread', label: 'Сначала непрочитанные' },
                                { value: 'name', label: 'По имени' },
                            ]}
                        />
                        <Tooltip title={searchOpen ? 'Закрыть поиск' : 'Поиск по чатам и переписке'}>
                            <button
                                type="button"
                                className={searchOpen ? 'lead-sort-search is-active' : 'lead-sort-search'}
                                onClick={() => setSearchOpen(!searchOpen)}
                            >
                                {searchOpen ? <CloseOutlined /> : <SearchOutlined />}
                            </button>
                        </Tooltip>
                    </div>

                    {searchOpen && (
                        <Input
                            autoFocus
                            allowClear
                            value={searchQuery}
                            placeholder="Поиск по чатам и переписке..."
                            onChange={(event) => setSearchQuery(event.target.value)}
                            onKeyDown={(event) => { if (event.key === 'Escape') setSearchOpen(false) }}
                        />
                    )}


                    {searchQuery.trim().length >= SEARCH_MIN_LENGTH && (
                        <div className="lead-search-hits">
                            <div className="lead-search-hits-head">
                                {/* Точное число совпадений не считаем: на большой базе
                                    подсчёт дороже самой выдачи. */}
                                НАЙДЕНО В ПЕРЕПИСКЕ{searchingMessages
                                    ? '…'
                                    : ` (${messageHits.length}${messageHits.length >= SEARCH_HITS_LIMIT ? '+' : ''})`}
                            </div>
                            {!searchingMessages && searchError && (
                                <div className="lead-search-empty">{searchError}</div>
                            )}
                            {!searchingMessages && !searchError && messageHits.length === 0 && (
                                <div className="lead-search-empty">Ничего не найдено</div>
                            )}
                            {messageHits.map((hit) => (
                                <button
                                    key={hit.message_id}
                                    type="button"
                                    className="lead-search-hit"
                                    onClick={() => openSearchHit(hit)}
                                >
                                    <span className="lead-search-hit-head">
                                        <b>{hit.lead_name}</b>
                                        <span>{dayjs(hit.date).format('DD.MM HH:mm')}</span>
                                    </span>
                                    <span className="lead-search-hit-text">
                                        {hit.outgoing ? 'Оператор: ' : ''}{hit.text}
                                    </span>
                                </button>
                            ))}
                        </div>
                    )}

                    <div
                        className="lead-chat-list-scroll"
                        onScroll={(event) => {
                            // Догружаем следующую порцию, когда до конца списка
                            // осталось меньше двух экранов.
                            const el = event.currentTarget
                            if (el.scrollHeight - el.scrollTop - el.clientHeight < el.clientHeight) {
                                void loadMoreWorkItems()
                            }
                        }}
                    >
                        <Spin spinning={loadingLeads}>
                            <List
                                dataSource={visibleWorkItems}
                                locale={{ emptyText: <Empty description="В этой вкладке пока пусто" /> }}
                                renderItem={(item) => (
                                    <List.Item
                                        className={`lead-chat-list-item${item.crm_fraud ? ' is-fraud' : ''}`}
                                        onClick={() => selectWorkItem(item)}
                                        onContextMenu={(event) => {
                                            if (!item.chat_id) return
                                            event.preventDefault()
                                            const position = clampContextMenuPosition(event.clientX, event.clientY)
                                            const lead = item.lead_id ? ({ id: item.lead_id } as Lead) : null
                                            setLeadContextMenu({ x: position.x, y: position.y, chatId: item.chat_id, lead })
                                        }}
                                        style={{
                                            cursor: 'pointer',
                                            padding: '10px 12px',
                                            borderLeft: `3px solid ${item.crm_fraud ? '#cf1322' : temperatureColor(item.temperature)}`,
                                            background: selectedItem?.chat_id === item.chat_id ? 'var(--bg-subtle)' : undefined,
                                        }}
                                    >
                                        <div className="chat-row-inner">
                                            <Avatar size={34} src={item.photo_url || undefined} style={{ flex: 'none', background: 'var(--bg-muted)', fontSize: 13 }}>
                                                {item.title.slice(0, 2).toUpperCase()}
                                            </Avatar>
                                            <div className="chat-row-body">
                                                <div className="chat-row-top">
                                                    <span className="chat-row-name">{item.title}</span>
                                                    <span className="chat-row-time">{item.last_message_at ? dayjs(item.last_message_at).format('HH:mm') : ''}</span>
                                                </div>
                                                <div className="chat-row-preview">
                                                    {item.work_state === 'postponed' && item.work_comment
                                                        ? `Отложен: ${item.work_comment}`
                                                        : item.work_state === 'closed' && item.close_outcome
                                                            ? `Итог: ${item.close_outcome_label || item.close_outcome}`
                                                            : (item.last_message_text
                                                                || item.username
                                                                || (item.crm_ww_id ? `ID CRM WW ${item.crm_ww_id}` : '')
                                                                || 'Сообщений пока нет')}
                                                </div>
                                                <div className="chat-row-tags">
                                                    {item.temperature && (
                                                        <span className="chat-chip" style={{ color: temperatureColor(item.temperature), borderColor: temperatureColor(item.temperature) }}>
                                                            {temperatureLabel(item.temperature)}
                                                        </span>
                                                    )}
                                                    {item.geo && <span className="chat-chip">{item.geo}</span>}
                                                    {foldersEnabled && item.lead_id && folderById.get(folderAssignments[String(item.lead_id)]) && (
                                                        <span className="chat-chip" style={{
                                                            color: folderById.get(folderAssignments[String(item.lead_id)])!.color,
                                                            borderColor: folderById.get(folderAssignments[String(item.lead_id)])!.color,
                                                        }}>
                                                            {folderById.get(folderAssignments[String(item.lead_id)])!.name}
                                                        </span>
                                                    )}
                                                    {!item.lead_id && <span className="chat-chip">без карточки</span>}
                                                    {!item.lead_id && <DuplicateChip check={item.duplicate} />}
                                                    {item.crm_fraud && (
                                                        <span className="chat-chip" style={{ color: '#cf1322', borderColor: '#cf1322' }}>
                                                            {item.crm_status_label || 'Фрод'}
                                                        </span>
                                                    )}
                                                    {item.unread_count > 0 && <span className="chat-unread">{item.unread_count}</span>}
                                                </div>
                                            </div>
                                        </div>
                                    </List.Item>
                                )}
                            />
                            {(loadingMoreWork || hasMoreWork) && (
                                <div style={{ padding: '10px 12px', textAlign: 'center' }}>
                                    <Text type="secondary" style={{ fontSize: 12 }}>
                                        {loadingMoreWork ? 'Загружаем ещё…' : 'Прокрутите вниз, чтобы загрузить ещё'}
                                    </Text>
                                </div>
                            )}
                        </Spin>
                    </div>
                </Space>
            </aside>

            <main className="lead-chat-main" style={{ display: 'grid', gridTemplateRows: 'auto auto minmax(0, 1fr) auto', minWidth: 0, paddingLeft: 22 }}>
                {selectedLead ? (
                    <>
                        <div className={`chat-detail-head${selectedItem?.crm_fraud ? ' is-fraud' : ''}`}>
                            <Avatar size={34} src={selectedLead.telegram_avatar_url || undefined} style={{ flex: 'none', background: 'var(--bg-muted)', fontSize: 14 }}>
                                {leadName(selectedLead).slice(0, 2).toUpperCase()}
                            </Avatar>
                            <div className="chat-detail-info">
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                                    <span className="chat-detail-name">{leadName(selectedLead)}</span>
                                    {selectedLead.temperature && (
                                        <span className="chat-chip" style={{
                                            color: temperatureColor(selectedLead.temperature),
                                            borderColor: temperatureColor(selectedLead.temperature),
                                        }}>
                                            {temperatureLabel(selectedLead.temperature)}
                                        </span>
                                    )}
                                </div>
                                <div className="chat-detail-meta">
                                    {[
                                        selectedLead.contacts_hidden ? leadRef(selectedLead) : leadUsername(selectedLead),
                                        selectedLead.geo,
                                        // Ответственный - профиль (команда), рядом - кто ведёт.
                                        selectedLead.visibility_profile_name || selectedLead.assigned_to_name,
                                        selectedLead.visibility_profile_name && selectedLead.assigned_to_name
                                            && `ведёт: ${selectedLead.assigned_to_name}`,
                                    ].filter(Boolean).join(' · ')}
                                </div>
                                {/* Статус лида в CRM Wiseway: последний прочитанный,
                                    обновляется фоновым проходом раз в 10 минут. */}
                                {selectedItem?.crm_status_label && (
                                    <div className="chat-detail-meta">
                                        Статус: <b style={selectedItem.crm_fraud ? { color: '#cf1322' } : undefined}>{selectedItem.crm_status_label}</b>
                                        {crmStatusStale?.stale && (
                                            <Tooltip title={
                                                crmStatusStale.minutes_ago === null
                                                    ? 'Статусы из CRM Wiseway ещё ни разу не прочитаны с момента запуска'
                                                    : `Последнее успешное чтение статусов из CRM Wiseway — ${crmStatusStale.minutes_ago} мин назад. Показанный статус мог измениться.`
                                            }>
                                                <span style={{ marginLeft: 6, color: '#d48806', cursor: 'help' }}>
                                                    · данные устарели
                                                </span>
                                            </Tooltip>
                                        )}
                                    </div>
                                )}
                            </div>

                            <div className="chat-detail-actions">
                                {activeChatId && activeWorkState === 'none' && (
                                    <button type="button" className="chat-btn-accent" disabled={actionLoading}
                                        onClick={() => runChatAction(
                                            () => chatWorkspaceApi.take(activeChatId),
                                            'Диалог взят в работу',
                                            { keepOpen: true, nextState: 'active', goToTab: 'active' },
                                        )}>
                                        Взять в работу
                                    </button>
                                )}
                                {activeChatId && (activeWorkState === 'postponed' || activeWorkState === 'closed') && (
                                    <button
                                        type="button"
                                        className="chat-btn-accent"
                                        disabled={actionLoading}
                                        onClick={() => {
                                            // Закрытый лид лежит в архиве Wiseway - нужно выбрать,
                                            // куда его вернуть. Отложенный там никуда не уходил.
                                            if (activeWorkState === 'closed') {
                                                setReopenOutcome(undefined)
                                                setReopenComment('')
                                                setReopenOpen(true)
                                                return
                                            }
                                            runChatAction(
                                                () => chatWorkspaceApi.reopen(activeChatId),
                                                'Диалог возвращён в работу',
                                                { keepOpen: true, nextState: 'active', goToTab: 'active' },
                                            )
                                        }}
                                    >
                                        {activeWorkState === 'closed' ? 'Переоткрыть' : 'Вернуть в работу'}
                                    </button>
                                )}
                                {activeChatId && activeWorkState === 'active' && (
                                    <>
                                        <button type="button" className="chat-btn-ghost" onClick={() => { setPostponeComment(''); setPostponeOpen(true) }}>
                                            Отложить
                                        </button>
                                        <button type="button" className="chat-btn-ghost is-danger" onClick={() => { setCloseComment(''); setCloseOutcome(undefined); setCloseOpen(true) }}>
                                            Закрыть
                                        </button>
                                    </>
                                )}
                                {activeChatId && (
                                    <button type="button" className="chat-btn-ghost" onClick={openTransfer}>
                                        Передать
                                    </button>
                                )}
                                {activeChatId && (
                                    <button type="button" className="chat-btn-ghost" onClick={openCrmStatus}>
                                        Статус
                                    </button>
                                )}
                                {/* «Заметки» - короткая сводка с заметками справа от чата,
                                    «Показать карточку» - полная карточка, как в очереди рассылок. */}
                                <button type="button" className="chat-btn-ghost" onClick={() => setCardPanelOpen((open) => !open)}>
                                    {cardPanelOpen ? 'Скрыть заметки' : 'Заметки'}
                                </button>
                                <button
                                    type="button"
                                    className="chat-btn-ghost"
                                    onClick={() => {
                                        if (cardDrawerLeadId !== selectedLead.id) {
                                            setCardDrawerLeadId(selectedLead.id)
                                            return
                                        }
                                        setCardDrawerLeadId(null)
                                        // Как при закрытии крестиком: правки в карточке могли
                                        // поменять то, что видно в чате.
                                        leadsApi.getLead(selectedLead.id)
                                            .then((response) => refreshCurrentLead(response.data))
                                            .catch(() => undefined)
                                    }}
                                >
                                    {cardDrawerLeadId === selectedLead.id ? 'Скрыть карточку' : 'Показать карточку'}
                                </button>
                            </div>

                            <Space className="lead-chat-header-actions" wrap>
                                {/* Заблокированного лида надо уметь и разблокировать:
                                    та же кнопка меняет смысл, а не гаснет навсегда. */}
                                {user?.can_block_leads !== false && (
                                    isOperatorBlockedLead(selectedLead) ? (
                                        <Tooltip title="Разблокировать лида">
                                            <Button
                                                type="text"
                                                shape="circle"
                                                className="lead-chat-action-btn is-danger"
                                                icon={<StopOutlined />}
                                                onClick={unblockSelectedLead}
                                                loading={sending}
                                                aria-label="Разблокировать лида"
                                            />
                                        </Tooltip>
                                    ) : (
                                        <Tooltip title="Заблокировать лида">
                                            <Button
                                                type="text"
                                                shape="circle"
                                                className="lead-chat-action-btn"
                                                icon={<StopOutlined />}
                                                onClick={blockSelectedLead}
                                                loading={sending}
                                                aria-label="Заблокировать лида"
                                            />
                                        </Tooltip>
                                    )
                                )}
                                {user?.can_delete_leads !== false && (
                                    <Popconfirm
                                        title="Удалить лида?"
                                        description="Лид и его карточка будут удалены из CRM."
                                        okText="Удалить"
                                        cancelText="Отмена"
                                        onConfirm={deleteSelectedLead}
                                    >
                                        <Tooltip title="Удалить лида">
                                            <Button
                                                type="text"
                                                shape="circle"
                                                className="lead-chat-action-btn is-danger"
                                                icon={<DeleteOutlined />}
                                                aria-label="Удалить лида"
                                            />
                                        </Tooltip>
                                    </Popconfirm>
                                )}
                            </Space>
                        </div>

                        {/* Все плашки над перепиской - одной строкой сетки. Каждая
                            отдельно занимала бы свою строку, и при двух плашках
                            сразу переписка наезжала на вторую. */}
                        <div className="lead-chat-banners">
                        {/* Лид ответил, а статус в CRM остался ожидающим: сами статус
                            не меняем - что ставить, решает оператор по ответу. */}
                        {selectedItem?.needs_status_update && (
                            <Alert
                                type="info"
                                showIcon
                                style={{ margin: '0 16px 8px' }}
                                message="Лид ответил — смените статус на актуальный"
                                description={selectedItem.status_hint_label
                                    ? `В CRM Wiseway он всё ещё «${selectedItem.status_hint_label}».`
                                    : undefined}
                                action={(
                                    <Button size="small" type="primary" onClick={openCrmStatus}>
                                        Изменить статус
                                    </Button>
                                )}
                            />
                        )}

                        {/* В чате - только то, что само не пройдёт (красный кружок).
                            Временное ограничение (жёлтый) чат не загораживает: его
                            подробности открываются нажатием на кружок внизу. */}
                        {accountKind === 'permanent' && accountIssue?.reason && !accountAlertHidden && (
                            <Alert
                                type="error"
                                showIcon
                                closable
                                onClose={() => setAccountAlertHidden(true)}
                                style={{ margin: '0 16px 8px' }}
                                message={accountIssueTitle}
                                description={accountIssueDescription}
                                action={accountIssueAction}
                            />
                        )}

                        <div className={currentPinnedMessage ? 'lead-pinned-bar' : 'lead-pinned-placeholder'}>
                            {currentPinnedMessage && (
                                <>
                                    <button type="button" className="lead-pinned-bar-main" onClick={jumpToPinnedMessage}>
                                        <span className="lead-pinned-accent" />
                                        <div>
                                            <Text strong>
                                                Закреплено {pinnedJumpMessages.length > 1 ? `${(pinnedJumpIndex % pinnedJumpMessages.length) + 1}/${pinnedJumpMessages.length}` : ''}
                                            </Text>
                                            <Text type="secondary">
                                                {currentPinnedMessage.text || currentPinnedMessage.media_file_name || `Сообщение #${currentPinnedMessage.tg_message_id || currentPinnedMessage.id}`}
                                            </Text>
                                        </div>
                                    </button>
                                    <Tooltip title="Открепить это сообщение">
                                        <Button
                                            type="text"
                                            shape="circle"
                                            className="lead-pinned-unpin"
                                            icon={<PushpinOutlined />}
                                            onClick={() => unpinMessage(currentPinnedMessage)}
                                        />
                                    </Tooltip>
                                </>
                            )}
                        </div>
                        </div>

                        <div className="lead-chat-messages-wrap">
                            <Spin spinning={loadingMessages}>
                                <div className="lead-chat-messages" ref={messagesContainerRef} onScroll={handleMessagesScroll}>
                                    {messagesList.length === 0 ? (
                                        <Empty description="История сообщений пока пустая" />
                                    ) : (
                                        <Space direction="vertical" size={10} style={{ width: '100%' }}>
                                            {hasEarlierMessages && (
                                                <Button size="small" block onClick={() => void loadEarlierMessages()} loading={loadingEarlier}>
                                                    Загрузить более ранние сообщения
                                                </Button>
                                            )}
                                            {messagesList.map((item) => (
                                                <div
                                                    id={`lead-message-${item.id}`}
                                                    key={item.id}
                                                    className={`lead-message-row ${item.outgoing ? 'is-outgoing' : 'is-incoming'} ${highlightedMessageId === item.id ? 'is-highlighted' : ''}`}
                                                    onContextMenu={(event) => {
                                                        event.preventDefault()
                                                        const position = clampContextMenuPosition(event.clientX, event.clientY)
                                                        setContextMenu({ ...position, item })
                                                    }}
                                                >
                                                    <div className={`lead-message-bubble${item.deleted_by_lead ? ' is-deleted-by-lead' : ''}`}>
                                                        {item.is_pinned && <Tag color="gold" style={{ marginBottom: 6 }}>Закреплено</Tag>}
                                                        {item.reply_to && (
                                                            <button
                                                                type="button"
                                                                className="lead-reply-line"
                                                                onClick={() => jumpToMessage(item.reply_to)}
                                                            >
                                                                Ответ на сообщение #{item.reply_to}
                                                            </button>
                                                        )}
                                                        {renderLeadMedia(item, ({ url, name }) => setImagePreview({ url, name, scale: 1, rotation: 0 }))}
                                                        {item.text && (
                                                            <Text className="lead-message-text">
                                                                {item.text_html ? <FormattedText html={item.text_html} /> : item.text}
                                                            </Text>
                                                        )}
                                                        <div className="lead-message-meta">
                                                            <span>
                                                                {item.is_edited && <span className="lead-message-edited">изм.</span>}
                                                                {item.deleted_by_lead && <span className="lead-message-edited">удалено лидом</span>}
                                                                <Tooltip title={messageAuthorTooltip(item)} overlayClassName="lead-message-author-tooltip">
                                                                    <QuestionCircleOutlined className="lead-message-author-hint" />
                                                                </Tooltip>
                                                                {' · '}{dayjs(item.date).format('DD.MM HH:mm')}
                                                            </span>
                                                            <DeliveryStatus item={item} />
                                                        </div>
                                                        {(item.reactions?.length || 0) > 0 && (
                                                            <div className="lead-message-reactions">
                                                                {item.reactions!.map((reaction) => {
                                                                    // Собеседников в диалоге лида ровно двое: chosen - наша
                                                                    // сторона, всё остальное поставил лид.
                                                                    const authors = reactionAuthors(reaction, item, selectedLead)
                                                                    return (
                                                                        <Tooltip key={reaction.emoji} title={authors.map((author) => author.title).join(' и ')}>
                                                                            <button
                                                                                type="button"
                                                                                className={reaction.chosen ? 'lead-reaction-chip is-own' : 'lead-reaction-chip'}
                                                                                onClick={() => toggleReaction(item, reaction.emoji)}
                                                                            >
                                                                                <span className="lead-reaction-faces">
                                                                                    {authors.map((author) => (author.avatar ? (
                                                                                        <img key={author.key} src={author.avatar} alt="" />
                                                                                    ) : (
                                                                                        <i key={author.key}>{author.initials}</i>
                                                                                    )))}
                                                                                </span>
                                                                                <span>{displayReaction(reaction.emoji)}</span>
                                                                                {reaction.count > 2 && <b>{reaction.count}</b>}
                                                                            </button>
                                                                        </Tooltip>
                                                                    )
                                                                })}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                            <div ref={messagesEndRef} />
                                        </Space>
                                    )}
                                </div>
                            </Spin>
                            {showScrollBottom && (
                                <Button
                                    type="primary"
                                    shape="circle"
                                    className="lead-scroll-bottom"
                                    icon={<DownOutlined />}
                                    onClick={() => scrollToBottom()}
                                />
                            )}
                        </div>

                        <div className="lead-chat-composer" onPasteCapture={handleComposerPaste}>
                            {editingMessage && (
                                <div className="lead-edit-preview">
                                    <EditOutlined />
                                    <span>Редактирование: {editingMessage.text || 'сообщение'}</span>
                                    <Button size="small" type="text" onClick={() => {
                                        setEditingMessage(null)
                                        setText('')
                                    }}>
                                        ×
                                    </Button>
                                </div>
                            )}
                            {replyTo && !editingMessage && (
                                <div className="lead-reply-preview">
                                    <div className="lead-reply-preview-body">
                                        <span>Ответ: {replyTo.text || replyTo.media_file_name || 'Медиа-сообщение'}</span>
                                        {quoteAccountId && quoteAccountId !== (senderId ?? senderInfo?.main_account_id) && (
                                            <div className="sender-note">
                                                Уйдёт с {findSender(quoteAccountId)?.label || 'аккаунта, на который пришло сообщение'}: цитировать можно только с аккаунта, на который пришло сообщение
                                            </div>
                                        )}
                                    </div>
                                    <Button size="small" type="text" onClick={() => setReplyTo(null)}>×</Button>
                                </div>
                            )}
                            {showSenderHint && lastIncomingSender && !editingMessage && (
                                <div className="sender-hint">
                                    <InfoCircleOutlined />
                                    <span className="sender-hint-text">
                                        Лид последним написал на <strong>{lastIncomingSender.label}</strong>,
                                        а ответ сейчас уйдёт с <strong>{effectiveSender?.label || 'основного аккаунта'}</strong>.
                                    </span>
                                    {lastIncomingSender.state !== 'unusable' && (
                                        <Button size="small" onClick={() => { setSenderId(lastIncomingSender.id); setSenderError(null) }}>
                                            Ответить с {lastIncomingSender.label}
                                        </Button>
                                    )}
                                    <Button
                                        size="small"
                                        type="text"
                                        icon={<CloseOutlined />}
                                        aria-label="Скрыть подсказку"
                                        onClick={() => setSenderHintDismissed(senderHintKey)}
                                    />
                                </div>
                            )}
                            {senderError && !editingMessage && (
                                <div className="sender-hint is-error">
                                    <InfoCircleOutlined />
                                    <span className="sender-hint-text">{senderError}. Текст сохранён - выберите другой аккаунт.</span>
                                    <Button size="small" type="text" icon={<CloseOutlined />} aria-label="Скрыть" onClick={() => setSenderError(null)} />
                                </div>
                            )}
                            {selectedFile && (
                                <div className="lead-attachment-preview">
                                    <FileOutlined />
                                    <span>{selectedFile.name}</span>
                                    <Button size="small" type="text" onClick={() => {
                                        setSelectedFile(null)
                                        if (fileInputRef.current) fileInputRef.current.value = ''
                                    }}>
                                        Убрать
                                    </Button>
                                </div>
                            )}
                            <div className="lead-composer-account">
                                <span className="lead-composer-account-from">
                                    {multiSender ? (
                                        <>
                                            Отправка от:
                                            <Tooltip title={replyTo ? 'Пока есть цитата, ответ уходит с аккаунта, на который пришло сообщение. Уберите цитату, чтобы выбрать другой.' : undefined}>
                                                <Select
                                                    size="small"
                                                    variant="borderless"
                                                    className="sender-select"
                                                    popupClassName="sender-select-dropdown"
                                                    popupMatchSelectWidth={false}
                                                    disabled={Boolean(replyTo)}
                                                    value={effectiveSenderId ?? undefined}
                                                    onChange={(value: number) => { setSenderId(value); setSenderError(null) }}
                                                    optionLabelProp="short"
                                                    options={senderAccounts.map((account) => senderOption(account, senderInfo?.last_incoming_account_id))}
                                                />
                                            </Tooltip>
                                        </>
                                    ) : (
                                        <>
                                    Отправка от: <strong>{accountLabelFromLead(selectedLead)}</strong>
                                    {/* Только кружок, без подписи. Красный при закрытом
                                        предупреждении возвращает его в чат; в остальных
                                        случаях открывает окошко с состоянием аккаунта. */}
                                    {accountKind === 'permanent' && accountIssue?.reason ? (
                                        <button
                                            type="button"
                                            className="chat-health-dot-button"
                                            aria-label={accountStateLabel}
                                            onClick={() => setAccountAlertHidden(false)}
                                        >
                                            <span className="chat-health-dot" style={{ background: accountDotColor }} />
                                        </button>
                                    ) : (
                                        <Popover
                                            trigger="click"
                                            placement="topLeft"
                                            // Причина блокировки - длинный текст ошибки Telegram:
                                            // без ограничения ширины окошко уходило за край экрана.
                                            overlayClassName="chat-health-popover"
                                            title={accountIssue?.reason ? accountIssueTitle : accountStateLabel}
                                            content={accountIssue?.reason ? (
                                                <div style={{ maxWidth: 420 }}>
                                                    <div>{accountIssueDescription}</div>
                                                    {accountIssueAction && <div style={{ marginTop: 10 }}>{accountIssueAction}</div>}
                                                </div>
                                            ) : (
                                                <div style={{ maxWidth: 420 }}>
                                                    {accountKind === 'ok'
                                                        ? 'Сообщения уходят как обычно.'
                                                        : 'Проверяем состояние аккаунта…'}
                                                </div>
                                            )}
                                        >
                                            <button type="button" className="chat-health-dot-button" aria-label={accountStateLabel}>
                                                <span className="chat-health-dot" style={{ background: accountDotColor }} />
                                            </button>
                                        </Popover>
                                    )}
                                        </>
                                    )}
                                </span>
                                {templatesEnabled && (
                                    <button
                                        type="button"
                                        className="lead-template-link"
                                        onClick={() => (templatesOpen ? closeTemplates() : openTemplates())}
                                    >
                                        Шаблоны <kbd>Tab</kbd>
                                    </button>
                                )}
                            </div>

                            {templatesEnabled && templatesOpen && (
                                <div className="lead-template-panel" onKeyDown={handleTemplateKeyDown}>
                                    <Input
                                        className="lead-template-search"
                                        value={templateQuery}
                                        onChange={(event) => { setTemplateQuery(event.target.value); setTemplateIndex(0) }}
                                        placeholder="Поиск шаблона..."
                                        allowClear
                                    />
                                    <div className="lead-template-scroll">
                                        {filteredTemplates.length === 0 ? (
                                            <div className="lead-template-empty">
                                                {templates.length ? 'Ничего не найдено' : 'Шаблоны ещё не заведены'}
                                            </div>
                                        ) : templateGroups.map(([category, items]) => (
                                            <div className="lead-template-group" key={category}>
                                                <div className="lead-template-group-title">{category}</div>
                                                {items.map((template) => {
                                                    const index = filteredTemplates.indexOf(template)
                                                    return (
                                                        <button
                                                            key={template.id}
                                                            type="button"
                                                            className={index === templateIndex ? 'lead-template-row is-active' : 'lead-template-row'}
                                                            onMouseEnter={() => setTemplateIndex(index)}
                                                            onClick={() => insertTemplate(template)}
                                                        >
                                                            <span className="lead-template-name">{template.name}</span>
                                                            <span className="lead-template-text">
                                                                {template.is_formatted ? <FormattedText html={template.content} /> : template.content}
                                                            </span>
                                                        </button>
                                                    )
                                                })}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                            <div className="lead-composer-row">
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    hidden
                                    onChange={(event) => selectAttachment(event.target.files?.[0] || undefined)}
                                />
                                <Tooltip title="Прикрепить файл">
                                    <Button
                                        className="lead-composer-icon"
                                        icon={<PaperClipOutlined />}
                                        onClick={() => fileInputRef.current?.click()}
                                        disabled={Boolean(editingMessage)}
                                    />
                                </Tooltip>
                                <Popover
                                    trigger="click"
                                    open={emojiOpen}
                                    onOpenChange={setEmojiOpen}
                                    placement="topLeft"
                                    overlayClassName="lead-emoji-popover"
                                    content={
                                        <RussianEmojiPicker
                                            onSelect={(emoji) => {
                                                composerRef.current?.insertText(emoji)
                                                setEmojiOpen(false)
                                            }}
                                        />
                                    }
                                >
                                    <Button className="lead-composer-icon" icon={<SmileOutlined />} />
                                </Popover>
                                {/* Поле с форматированием как в Telegram: выделил текст -
                                    над полем появилась панель (жирный, курсив, цитата...). */}
                                <TgRichEditor
                                    ref={composerRef}
                                    className="lead-composer-input"
                                    value={text}
                                    onChange={setText}
                                    toolbar="selection"
                                    minRows={1}
                                    maxRows={5}
                                    onKeyDown={(event) => {
                                        // Tab перехватываем только в поле сообщения и только когда
                                        // шаблоны включены. Shift+Tab не трогаем - им уходят с поля.
                                        if (event.key === 'Tab' && !event.shiftKey && templatesEnabled) {
                                            event.preventDefault()
                                            openTemplates()
                                            return
                                        }
                                        // Enter отправляет, Shift+Enter - перенос строки.
                                        if (event.key === 'Enter' && !event.shiftKey) {
                                            event.preventDefault()
                                            sendMessage()
                                        }
                                    }}
                                    placeholder={editingMessage ? 'Редактирование сообщения...' : selectedFile ? 'Подпись к файлу...' : 'Сообщение...'}
                                />
                                <Button
                                    type="primary"
                                    className="lead-composer-send"
                                    icon={<SendOutlined />}
                                    loading={sending}
                                    disabled={editingMessage ? !composerPlain : (!composerPlain && !selectedFile)}
                                    onClick={() => sendMessage()}
                                />
                            </div>
                        </div>

                        {contextMenu && (
                            <div
                                className="lead-reaction-bar"
                                style={{ left: contextMenu.x, top: Math.max(8, contextMenu.y - 52) }}
                                onClick={(event) => event.stopPropagation()}
                            >
                                {visibleQuickReactions.map((emoji) => (
                                    <button key={emoji} type="button" onClick={() => toggleReaction(contextMenu.item, emoji)}>
                                        {displayReaction(emoji)}
                                    </button>
                                ))}
                                <span className="lead-reaction-bar-divider" />
                                <button
                                    type="button"
                                    className="lead-reaction-more"
                                    title="Выбрать другую реакцию"
                                    onClick={() => { setReactionPickerFor(contextMenu.item); setReactionQuery(''); setContextMenu(null) }}
                                >+</button>
                            </div>
                        )}

                        <Modal
                            open={Boolean(reactionPickerFor)}
                            title="Выбрать реакцию"
                            footer={null}
                            width={420}
                            className="lead-reaction-picker-modal"
                            onCancel={() => setReactionPickerFor(null)}
                            destroyOnClose
                        >
                            <div className="lead-reaction-picker">
                                <Input
                                    value={reactionQuery}
                                    onChange={(event) => setReactionQuery(event.target.value)}
                                    placeholder="Поиск реакции..."
                                    allowClear
                                />
                                <div className="lead-reaction-grid">
                                    {filteredAvailableReactions.map((item) => (
                                        <button
                                            key={item.emoji}
                                            type="button"
                                            title={item.title || undefined}
                                            onClick={() => { if (reactionPickerFor) toggleReaction(reactionPickerFor, item.emoji) }}
                                        >
                                            {displayReaction(item.emoji)}
                                        </button>
                                    ))}
                                </div>
                                {filteredAvailableReactions.length === 0 && (
                                    <div className="lead-reaction-empty">Ничего не найдено</div>
                                )}
                            </div>
                        </Modal>

                        {contextMenu && (
                            <div className="lead-message-context-menu" style={{ left: contextMenu.x, top: contextMenu.y }} onClick={(event) => event.stopPropagation()}>
                                <button type="button" onClick={() => { setReplyTo(contextMenu.item); setContextMenu(null) }}><span>↩</span>Ответить</button>
                                <button type="button" onClick={() => copyMessageText(contextMenu.item)}><span>📋</span>Копировать текст</button>
                                {contextMenu.item.outgoing && contextMenu.item.text && ['text', 'action'].includes(contextMenu.item.content_type) && (
                                    <button type="button" onClick={() => startEditMessage(contextMenu.item)}><span>✎</span>Редактировать</button>
                                )}
                                {contextMenu.item.is_pinned ? (
                                    <button type="button" onClick={() => unpinMessage(contextMenu.item)}><span>📌</span>Открепить</button>
                                ) : (
                                    <button type="button" onClick={() => pinMessage(contextMenu.item)}><span>📌</span>Закрепить</button>
                                )}
                                <div className="lead-message-context-divider" />
                                <button type="button" className="is-danger" onClick={() => deleteMessage(contextMenu.item, false)}><span>🗑</span>Удалить у себя</button>
                                <button type="button" className="is-danger" onClick={() => deleteMessage(contextMenu.item, true)}><span>🗑</span>Удалить у всех</button>
                            </div>
                        )}

                        <Modal
                            open={Boolean(imagePreview)}
                            footer={null}
                            centered
                            width="100vw"
                            className="lead-image-preview-modal"
                            onCancel={() => setImagePreview(null)}
                            destroyOnClose
                        >
                            {imagePreview && (
                                <div className="lead-image-preview">
                                    <div className="lead-image-toolbar">
                                        <Text strong>{imagePreview.name}</Text>
                                        <Space>
                                            <Button icon={<ZoomOutOutlined />} onClick={() => setImagePreview((current) => current ? { ...current, scale: Math.max(0.25, current.scale - 0.25) } : current)} />
                                            <Button icon={<ZoomInOutlined />} onClick={() => setImagePreview((current) => current ? { ...current, scale: Math.min(4, current.scale + 0.25) } : current)} />
                                            <Button icon={<RotateLeftOutlined />} onClick={() => setImagePreview((current) => current ? { ...current, rotation: current.rotation - 90 } : current)} />
                                            <Button icon={<RotateRightOutlined />} onClick={() => setImagePreview((current) => current ? { ...current, rotation: current.rotation + 90 } : current)} />
                                            <Button icon={<DownloadOutlined />} href={imagePreview.url} target="_blank" />
                                        </Space>
                                    </div>
                                    <div className="lead-image-stage">
                                        <img
                                            src={imagePreview.url}
                                            alt={imagePreview.name}
                                            style={{
                                                transform: `scale(${imagePreview.scale}) rotate(${imagePreview.rotation}deg)`,
                                            }}
                                        />
                                    </div>
                                </div>
                            )}
                        </Modal>
                    </>
                ) : selectedItem && !selectedItem.lead_id ? (
                    // На всю высоту области: шапка и поле ввода - сколько нужно,
                    // лента сообщений - остаток. Так низ окна не уезжает за край
                    // на невысоком экране или при крупном масштабе.
                    <div style={{ gridRow: '1 / -1', display: 'flex', flexDirection: 'column', gap: 12, width: '100%', height: '100%', minHeight: 0 }}>
                        <div className="lead-chat-header" style={{ flex: 'none' }}>
                            <Space align="center" size={12} wrap>
                                <Avatar size={48} src={selectedItem.photo_url || undefined} icon={<UserOutlined />} />
                                <div>
                                    <Title level={4} style={{ margin: 0 }}>{selectedItem.title}</Title>
                                    <Space size={8} wrap>
                                        <Tag color="purple">Нет карточки лида</Tag>
                                        <Text type="secondary">{selectedItem.username || 'Telegram не указан'}</Text>
                                        {selectedItem.account_username && (
                                            <Text type="secondary">через @{selectedItem.account_username}</Text>
                                        )}
                                    </Space>
                                </div>
                            </Space>
                            <div style={{ marginTop: 10 }}>
                                <DuplicateNotice check={selectedItem.duplicate} onRecheck={recheckDuplicate} rechecking={rechecking} />
                            </div>
                            <Space size={8} style={{ marginTop: 10 }} wrap>
                                {user?.can_create_lead_from_chat !== false && (
                                    <Button type="primary" onClick={openLeadDraft}>Добавить в лиды</Button>
                                )}
                            </Space>
                        </div>

                        <PlainChatWindow
                            chatId={selectedItem.chat_id}
                            onSent={() => {
                                // Ответил - значит взял: диалог мог уйти в «Активные».
                                void loadWorkItems()
                            }}
                        />
                    </div>
                ) : (
                    <Empty description="Выберите диалог из списка" />
                )}
            </main>

            <Modal
                open={reminderOpen}
                title="Напоминание"
                onCancel={() => setReminderOpen(false)}
                footer={null}
                width={420}
                destroyOnClose
            >
                <Space direction="vertical" size={14} style={{ width: '100%' }}>
                    <Space size={8} wrap>
                        {REMINDER_PRESETS.map((preset) => (
                            <Button key={preset.label} size="small" onClick={() => setReminderValue(preset.value())}>
                                {preset.label}
                            </Button>
                        ))}
                    </Space>

                    <DatePicker
                        showTime={{ format: 'HH:mm' }}
                        format="DD.MM.YYYY HH:mm"
                        style={{ width: '100%' }}
                        value={reminderValue}
                        onChange={(value) => setReminderValue(value)}
                        placeholder="Своя дата и время"
                    />

                    {reminderValue && (
                        <Text type={reminderValue.isBefore(dayjs()) ? 'danger' : 'secondary'}>
                            {reminderValue.isBefore(dayjs())
                                ? 'Указано прошедшее время - напоминание придёт сразу'
                                : `Напомним ${reminderValue.format('D MMMM, HH:mm')}`}
                        </Text>
                    )}

                    <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                        <Button danger type="text" disabled={noteSaving} onClick={() => applyReminder(null)}>
                            Без напоминания
                        </Button>
                        <Space>
                            <Button onClick={() => setReminderOpen(false)}>Отмена</Button>
                            <Button type="primary" loading={noteSaving} disabled={!reminderValue}
                                onClick={() => applyReminder(reminderValue)}>
                                Готово
                            </Button>
                        </Space>
                    </Space>
                </Space>
            </Modal>

            {cardPanelOpen && selectedLead && (
                <aside className="chat-card-panel" style={{ display: cardDrawerLeadId !== null ? 'none' : undefined }}>
                    <div className="chat-card-panel-head">
                        <span>КАРТОЧКА ЛИДА</span>
                        {/* Кнопка не зависит от «Скрывать раздел „Карточки лидов“»: тот
                            переключатель убирает список всех лидов, а здесь открывается
                            карточка того лида, с которым оператор уже работает. Правку
                            внутри по-прежнему решает «Запретить редактировать лида». */}
                        <button type="button" onClick={() => setCardDrawerLeadId(selectedLead.id)}>Открыть</button>
                    </div>
                    <dl className="chat-card-list">
                        <div><dt>ID лида</dt><dd>#{selectedLead.id}</dd></div>
                        <div><dt>Название</dt><dd>{selectedLead.title || '—'}</dd></div>
                        <div><dt>ID CRM WW</dt><dd>{selectedLead.crm_ww_id || '—'}</dd></div>
                        {/* Ник и телефон оператору не приходят с сервера: вместо
                            них он оперирует идентификатором карточки в CRM. */}
                        {!selectedLead.contacts_hidden && (
                            <div><dt>Telegram</dt><dd>{leadUsername(selectedLead)}</dd></div>
                        )}
                        <div><dt>ГЕО</dt><dd>{selectedLead.geo || '—'}</dd></div>
                        <div><dt>Температура</dt><dd>{temperatureLabel(selectedLead.temperature)}</dd></div>
                        {selectedItem?.crm_status_label && (
                            <div>
                                <dt>Статус CRM</dt>
                                <dd>
                                    {selectedItem.crm_status_label}
                                    {crmStatusStale?.stale && (
                                        <span style={{ marginLeft: 6, color: '#d48806' }}>
                                            {crmStatusStale.minutes_ago === null
                                                ? '(не прочитан)'
                                                : `(данные ${crmStatusStale.minutes_ago} мин назад)`}
                                        </span>
                                    )}
                                </dd>
                            </div>
                        )}
                        {!selectedLead.contacts_hidden && (
                            <div><dt>Телефон</dt><dd>{selectedLead.phone || '—'}</dd></div>
                        )}
                        <div><dt>Источник</dt><dd>{selectedLead.source || '—'}</dd></div>
                        {/* Ответственный - профиль (РП), как в карточке лида; рядом - кто ведёт. */}
                        <div><dt>Ответственный</dt><dd>{
                            selectedLead.visibility_profile_name
                                ? [selectedLead.visibility_profile_name, selectedLead.visibility_profile_email].filter(Boolean).join(' — ')
                                : (selectedLead.assigned_to_name || '—')
                        }</dd></div>
                        {selectedLead.visibility_profile_name && (
                            <div><dt>Ведёт</dt><dd>{selectedLead.assigned_to_name || 'никто из команды'}</dd></div>
                        )}
                        {/* Аккаунт берём из диалога, а если список чатов ещё не
                            подгрузился или диалог лежит в другой вкладке -
                            из карточки лида: там то же значение. */}
                        <div><dt>Аккаунт</dt><dd>{
                            selectedItem?.account_username || selectedLead.account_username
                                ? `@${selectedItem?.account_username || selectedLead.account_username}`
                                : (selectedItem?.account_phone || selectedLead.account_phone || '—')
                        }</dd></div>
                        {selectedItem?.close_outcome && (
                            <div><dt>Итог</dt><dd>{selectedItem.close_outcome_label || selectedItem.close_outcome}</dd></div>
                        )}
                    </dl>

                    <div className="chat-note-head">
                        ЗАМЕТКИ{notes.length ? ` (${notes.length})` : ''}
                    </div>
                    {notesLoading && <div className="chat-note-empty">Загрузка…</div>}
                    {!notesLoading && notes.length === 0 && <div className="chat-note-empty">Заметок пока нет</div>}
                    {sortedNotes.map((note) => {
                        const overdue = !!note.remind_at && dayjs(note.remind_at).isBefore(dayjs())
                        return (
                            <div key={note.id} className="chat-note">
                                {(
                                    <>
                                        <div className="chat-note-text">{note.text}</div>
                                        <div className="chat-note-foot">
                                            {note.remind_at && (
                                                <button
                                                    type="button"
                                                    className={overdue ? 'chat-note-remind is-overdue' : 'chat-note-remind'}
                                                    title="Перенести напоминание"
                                                    onClick={() => openReminderModal(note.id, note.remind_at)}
                                                >
                                                    ⏰ {overdue ? 'Просрочено · ' : ''}{dayjs(note.remind_at).format('DD.MM HH:mm')}
                                                </button>
                                            )}
                                            <span className="chat-note-author">
                                                {[note.author_name, dayjs(note.created_at).format('DD.MM HH:mm')].filter(Boolean).join(' · ')}
                                            </span>
                                            {note.can_delete && (
                                                <>
                                                    {!note.remind_at && (
                                                        <button type="button" className="chat-note-action"
                                                            onClick={() => openReminderModal(note.id, null)}>Напомнить</button>
                                                    )}
                                                    <button type="button" className="chat-note-delete" title="Удалить заметку"
                                                        onClick={() => removeNote(note.id)}>×</button>
                                                </>
                                            )}
                                        </div>
                                    </>
                                )}
                            </div>
                        )
                    })}

                    <div className="chat-note-form">
                        <textarea
                            value={noteDraft}
                            onChange={(event) => setNoteDraft(event.target.value)}
                            onKeyDown={(event) => {
                                if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) addNote()
                            }}
                            placeholder="Новая заметка…  (Ctrl+Enter — сохранить)"
                        />
                        <div className="chat-note-form-row">
                            <button
                                type="button"
                                className={noteRemindAt ? 'chat-note-remind-btn is-set' : 'chat-note-remind-btn'}
                                onClick={() => openReminderModal(null, noteRemindAt || null)}
                                title="Напомнить о заметке"
                            >
                                ⏰ {noteRemindAt ? dayjs(noteRemindAt).format('DD.MM HH:mm') : 'Напомнить'}
                            </button>
                            <button
                                type="button"
                                className="chat-note-add"
                                disabled={!noteDraft.trim() || noteSaving}
                                onClick={addNote}
                            >Сохранить</button>
                        </div>
                    </div>
                </aside>
            )}

        </div>

        {/* Та же карточка, что и в разделе «Карточки лидов»: страница
            переиспользуется во встроенном режиме, чтобы не держать вторую
            реализацию окна. Держим её за пределами сетки чата - иначе пустая
            обёртка становится её ячейкой и забирает высоту у переписки. */}
        {cardDrawerLeadId !== null && (
            <Contacts
                embeddedLeadId={cardDrawerLeadId}
                embeddedWidth={CARD_DRAWER_WIDTH}
                onEmbeddedClose={() => {
                    setCardDrawerLeadId(null)
                    // Правки в карточке могли поменять то, что видно в чате.
                    if (selectedLead) {
                        leadsApi.getLead(selectedLead.id)
                            .then((response) => refreshCurrentLead(response.data))
                            .catch(() => undefined)
                    }
                }}
            />
        )}
        </>
    )
}
